import {
  Color3,
  CreateCylinder,
  CreateSphere,
  CreateTorus,
  Matrix,
  Mesh,
  Scene,
  StandardMaterial,
  UniversalCamera,
  Vector3,
  Viewport,
  type Engine,
  type TransformNode,
} from "@babylonjs/core";
import {
  BATTLE_ROYALE,
  CREW,
  SHIP_LADDERS,
  isScopeStation,
  maxBalls,
  ROYALE_ROOM_ID,
  royaleDistance,
  CANNON,
  ISLAND_CANNONS,
  type IslandId,
  ballPositionAt,
  bodyHeight,
  bodyEyeHeight,
  cannonShot,
  cannonAim,
  islandCannonAim,
  getIslandCannon,
  playerCannonShot,
  playerLaunchPositionAt,
  frameSlot,
  capstanBar,
  capstanStation,
  type CapstanStation,
  SUPPLY_BARRELS,
  FIXTURES,
  GATE_LEVERS,
  LOOT_CRATES,
  TELESCOPES,
  WORLD_LADDERS,
  worldColliders,
  supplyBarrelWorld,
  type CrewChoice,
  C2S,
  CHEST_CARRIED,
  CHEST_FLOATING,
  CHEST_SHIP,
  DT,
  WEATHER,
  createWeatherSample,
  smoothstep,
  weatherAt,
  type WeatherSample,
  type CrewState,
  FRAME_WORLD,
  INTERACT,
  LANTERN_BEARINGS,
  LANTERN_COLOURS,
  LANTERN_INTERACTIONS,
  LANTERN_RULES,
  MELEE,
  PISTOL,
  SPYGLASS,
  NET,
  OUTPOSTS,
  SHIP_SLOTS,
  getSeaStatus,
  homeOutpost,
  MODE_LAUNCH,
  MODE_SWIM,
  MODE_WALK,
  PLAYER,
  S2C,
  SHIP,
  SELF_CANNON,
  SHIP_POINTS,
  WORLD,
  getIsland,
  isIslandId,
  landmassAt,
  localToWorld,
  carriesLoad,
  overDeck,
  overboardThrow,
  terrainHeight,
  wrapAngle,
  worldVisible,
  type DigResultMsg,
  type IslandReadMsg,
  type Equipment,
  type FxMsg,
  type HoldTarget,
  type InputMsg,
  type MovementContext,
  type MeleeResultMsg,
  type AttackAckMsg,
  type PistolShotMsg,
  type PlayerState,
  type Pose,
  type SaleMsg,
  type ToastMsg,
  type UseTarget,
  type OverboardThrow,
  type Vec3,
  type RoyalePhase,
  decodeLook,
  repairStep,
  wheelBroken,
  type RepairStep,
} from "@tiny-seas/shared";
import { audio, type AmbienceFrame, type ShipAudioState, type Sfx } from "./audio.ts";
import { ACTION_SFX, ActionAudioTracker, MAX_CUES, MovementAudioTracker, shoreProximity, surfaceAt, type MotionCues, type MotionSample } from "./audio/gameplay.ts";
import { REPAIR_TARGETS, nearestRepair, repairPrompt, repairPromptPoint, repairStanding, repairingPrompt } from "./repairs.ts";
import { Input } from "./input.ts";
import { ServerClock, SnapshotBuffer, createPoseSample, type PoseSample } from "./interp.ts";
import type { GameRoom, Net } from "./net.ts";
import { Predictor } from "./predict.ts";
import { Avatar, ChestModel, LootModel, digMound, type AvatarLocomotion } from "./scene/actors.ts";
import { LOOK_STORAGE_KEY, savedLookCode } from "./scene/character-look.ts";
import { CharacterAnimator } from "./character/character-animator.ts";
import { createCharacterPose, resolveCharacterAction, type CharacterPose } from "./character/character-pose.ts";
import { DIG_SWING_SECONDS } from "./scene/character-rig.ts";
import { Effects } from "./scene/effects.ts";
import { SWORD_FLINCH_SECONDS } from "./scene/sword-body.ts";
import { Environment } from "./scene/environment.ts";
import { SURFACE_PROFILES, sphereProjectUVs } from "./scene/materials.ts";
import { ClimbPresentation, climbTarget, newClimbTarget } from "./scene/climb.ts";
import { BASE_VERTICAL_FOV, FirstPersonView, applyBodyToPose, bodySpeed, createFirstPersonFrame, lensMagnification } from "./scene/first-person-view.ts";
import { THIRD_PERSON, thirdPersonDistance } from "./scene/third-person-view.ts";
import { IslandCannonModel, ShipModel } from "./scene/ship.ts";
import type { HullSource } from "./scene/water-sim.ts";
import { phaseChip, royaleResults, royaleSplash, spectatorView, type RoyaleSnapshot } from "./royale-status.ts";
import { settings, type OnboardingStep } from "./settings.ts";
import { Hud, type CrewEntry, type CrewView, type OnlineView } from "./ui/hud.ts";
import type { MapMarker } from "./ui/maps.ts";


/** Server effects with a sound; the rest (milestone, respawn, drown) are intentionally silent here. `cannon` picks near/far in `onFx`. */
const FX_SOUND: Partial<Record<FxMsg["kind"], Sfx>> = {
  impact: "impact",
  ram: "ram",
  leak: "leak",
  repaired: "repaired",
  dig: "dig",
  treasure: "treasure",
  pickup: "pickup",
  drop: "drop",
  sold: "sale",
  sinking: "sinking",
  splash: "splash",
  bail: "bail",
  anchorDrop: "anchorDrop",
  anchorSplash: "anchorSplash",
  anchorClick: "anchorClick",
  anchorStowed: "anchor",
  sail: "sail",
  death: "death",
  ballHit: "ballHit",
  ballSplash: "ballSplash",
  ballLand: "ballLand",
  playerHit: "playerHit",
  restock: "restock",
  crateHit: "repair",
  crateBreak: "ballHit",
  gate: "anchor",
  bell: "bell",
  lantern: "lantern",
  ropeSnap: "ropeSnap",
  sailTear: "sailTear",
  wheelSmash: "wheelSmash",
  gunWreck: "gunWreck",
};

/** Beyond this the blast is heard as a far boom, after the sound's travel time. */
const CANNON_NEAR = 120;
const SPEED_OF_SOUND = 343;
/** A rival cannonball passing within this many metres of the listener whistles past. */
const WHIZ_RADIUS = 14;
/** Shortest gap between two danger stings (a broadside sends several "bad" toasts at once). */
const DANGER_GAP = 1500;
/** Ships farther than this from the listener have no wake/rigging/flooding bed. */
const SHIP_AUDIO_RANGE = 80;
const MAX_SHIP_BEDS = 4;
const MAX_SAIL_SPEED = Math.max(...SHIP.sailSpeeds);
/** A hold released within this many milliseconds counts as a tap. */
const TAP_MS = 250;
/**
 * The presented swing eases toward authority by running up to half again faster or slower;
 * only a drift beyond this many seconds (a stalled tab) re-times it outright.
 */
const ATTACK_CLOCK_SNAP = 0.5;

const newMotion = (): MotionSample => ({
  frame: 0, generation: -1, mode: 0, x: 0, y: 0, z: 0, grounded: true, enabled: false, carrying: false, sliding: false, crouched: false, sprinting: false, station: "",
});
const byDistanceThenSlot = (a: { slot: number; d: number }, b: { slot: number; d: number }): number => a.d - b.d || a.slot - b.slot;

interface Remote {
  avatar: Avatar;
  /** Look code the avatar was built from (PlayerState.look). */
  look: string;
  /** Floating name tag (DOM) with connection status. */
  tag: HTMLElement;
  buffer: SnapshotBuffer;
  epoch: number;
  last: { tick: number; frame: number; mode: number; launchSerial: number; x: number; y: number; z: number };
  sample: PoseSample;
  animator: CharacterAnimator;
  pose: CharacterPose;
  locomotion: AvatarLocomotion;
  gaitFrame: Matrix;
  prevX: number;
  prevZ: number;
  prevFrame: number;
  prevEpoch: number;
  prevShipEpoch: number;
  climb: ClimbPresentation;
  attackSerial: number;
  scopeBlend: number;
  recoil: number;
  parryRaise: number;
  /** Seconds since this sailor was struck by a cutlass, else -1; and that cut's combo step. */
  hitFlinch: number;
  hitStep: number;
  cannonRecoil: number;
  pistolAimBlend: number;
  pistolRecoil: number;
  motion: MovementAudioTracker;
  heldAction: ActionAudioTracker;
  motionPrev: MotionSample;
  motionNow: MotionSample;
}

interface Target {
  /** "crate" is prompt-only: crates are smashed with the cutlass, not used. Repairs (`rope:i`, `sail:i`…) are holds. */
  id: UseTarget | HoldTarget | "questBoard" | "mapTable" | "crate";
  hold: boolean;
  /** A hold that, released at once, uses this instead (man a damaged helm, or hold to mend it). */
  tap?: UseTarget;
  label: string;
  x: number;
  y: number;
  z: number;
  visible?: boolean;
  enabled?: boolean;
}

/** Automation hooks for browser tests (enabled with `?autotest=1`). */
export interface AutoInput {
  mx: number;
  mz: number;
  jump: boolean;
  dive: boolean;
  sprint: boolean;
  crouch: boolean;
  scope: boolean;
}

const tmp: Vec3 = { x: 0, y: 0, z: 0 };
const tmp2: Vec3 = { x: 0, y: 0, z: 0 };
const repairScratch: RepairStep = { point: SHIP_POINTS.wheel, seconds: 0, planks: 0 };


interface ClientShip {
  model: ShipModel;
  buffer: SnapshotBuffer;
  pose: PoseSample;
  server: Pose;
  epoch: number;
  generation: number;
  prevLeaks: number;
  /** Last seen `ShipState.bellRings`; a change swings the bell. */
  bellRings: number;
  tag: HTMLElement;
  /** World-space hull motion on the waves: maps flat ship-frame world points onto the riding hull. */
  ride: Matrix;
  /** This hull's entry in the environment's hull sources, reused every frame. */
  hull: HullSource;
}

/** Read-only network internals for the `?net=1` monitor (net-monitor.ts). */
export interface NetProbe {
  readonly room: GameRoom;
  readonly clock: ServerClock;
  readonly predictor: Predictor;
  /** Server tick the latest frame rendered (interpolation time). */
  renderTick(): number;
  /** Visits every snapshot buffer; `own` is the local crew's ship. */
  forEachBuffer(visit: (buffer: SnapshotBuffer, kind: "own" | "ship" | "player") => void): void;
}

export class GameClient {
  private readonly scene: Scene;
  private readonly env: Environment;
  private readonly ships = new Map<number, ClientShip>();
  private readonly islandCannons = new Map<IslandId, IslandCannonModel>();
  private readonly poses: (Pose | null)[] = Array.from({ length: SHIP_SLOTS }, () => null);
  private readonly renderPoses: (Pose | null)[] = Array.from({ length: SHIP_SLOTS }, () => null);
  private readonly rideFlat = new Matrix();
  private readonly rideHull = new Matrix();
  private readonly ridePoint = new Vector3();
  private readonly remoteFeet: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly cameraFeet: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly hullSources: HullSource[] = [];
  private readonly balls: Mesh[] = [];
  private readonly landingRing: Mesh;
  private readonly shotPosition: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly shotVelocity: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly shotShip = { x: 0, z: 0, heading: 0, speed: 0 };
  private readonly previewBall = { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0 };
  private ringColor = "";
  private readonly previewAim = { yaw: 0, elevation: 0 };
  private readonly previewPivot: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly previewInherited: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly previewCache = { station: "", loaded: false, yaw: NaN, pitch: NaN, x: NaN, z: NaN, heading: NaN, speed: NaN, valid: false };
  private readonly barrelLocal = new Vector3(0, 0, CANNON.barrelLength + 0.05);
  private readonly barrelWorld = new Vector3();
  private readonly barrelForward = new Vector3();
  private readonly barrelAxis = new Vector3(0, 0, 1);
  private observedLaunchSerial: number | null = null;
  private readonly camera: UniversalCamera;
  private readonly effects: Effects;
  private readonly hud: Hud;
  readonly input: Input;
  private readonly predictor = new Predictor();
  private readonly clock = new ServerClock();
  private readonly remotes = new Map<string, Remote>();
  private readonly chests = new Map<string, ChestModel>();
  /** Battle royale arena: the room plays the royale mode, its ring wall, treasure beacon and loot barrels. */
  private readonly royale: boolean;
  private readonly ringMesh: Mesh | null = null;
  private readonly treasureBeacon: Mesh | null = null;
  private readonly loot = new Map<string, LootModel>();
  private spectateSlot = -1;
  private wasQueued = false;
  /** Last battle royale snapshot, for transition splashes. */
  private royaleSnap: RoyaleSnapshot | null = null;
  private readonly mounds: Mesh[] = [];
  /** The local sailor: one full-body avatar for first and third person. */
  private selfAvatar: Avatar;
  /** Look code the self avatar was built from (PlayerState.look). */
  private selfLook = "";
  private readonly selfPose = createCharacterPose();
  private readonly selfAnimator = new CharacterAnimator();
  /** Interpolated feet (world), body-frame position and support frame, filled by updateCamera. */
  private readonly selfFrame = { feet: new Vector3(), localX: 0, localZ: 0, frame: 0 };
  private readonly selfLocomotion: AvatarLocomotion = { distance: 0, discontinuous: true, frameToWorld: null };
  private readonly selfGaitFrame = new Matrix();
  private selfPrevX = 0;
  private selfPrevZ = 0;
  private selfPrevFrame = -1;
  /** V toggles the local view; not persisted. */
  private view: "first" | "third" = "first";
  /** The chase camera is in use this frame; optics and death force first person. */
  private thirdPersonActive = false;
  private readonly chaseBack: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly chaseOrigin: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly climb = new ClimbPresentation();
  private readonly localClimb = newClimbTarget();
  private readonly remoteClimb = newClimbTarget();
  /** Gate/crate signature of the predictor's current world colliders. */
  private colliderKey = "";
  /** 0 … 1 blend into a mounted telescope's magnification. */
  private telescopeBlend = 0;
  private readonly telescopeObservers = TELESCOPES.map(() => ({ active: false, yaw: 0, pitch: 0 }));
  private readonly logicalEye = new Vector3();
  private readonly logicalForward = new Vector3();
  private readonly firstPerson = new FirstPersonView();
  private readonly viewFrame = createFirstPersonFrame();
  private sailBlend = 0;
  private eyeHeight: number = PLAYER.eyeHeight;
  private readonly capstanPose: CapstanStation = { x: 0, y: 0, z: 0, yaw: 0 };
  /** Rendered capstan turn the local pusher's view last followed; null when not on a bar. */
  private capstanViewTurn: number | null = null;
  private capstanSettle = 0;
  /** Rendered sea height the local surface swimmer rides this frame (camera and rig). */
  private selfHeave = 0;
  private ladderViewOffset = 0;
  private ladderViewHeading = 0;
  private shovelSwing = 0;
  private blockSent = false;
  private attackPrediction = 0;
  private predictedSerial = 0;
  /** Seconds into the presented local swing (-1: none) and the attack serial it presents. */
  private attackClock = -1;
  private attackClockSerial = 0;
  private attackRequestId = 0;
  private pendingAttackRequest = 0;
  private pendingQueueRequest = 0;
  private queuedSerial = 0;
  private strikeHitSerial = -1;
  private observedAttackSerial = 0;
  private playedAttackSerial = 0;
  private recoil = 0;
  private parryRaise = 0;
  private hitFlinch = -1;
  private hitStep = 0;
  private cannonRecoil = 0;
  private readonly meleeSeen = new Map<string, number>();
  private attackPhase = -1;
  private displayAttackStep = 0;
  private displayAttackDuration = 0;
  private scopeBlend = 0;
  private magnification = 1;
  private scopeSuppressedUntilRelease = false;
  private pistolAimBlend = 0;
  private pistolAimSuppressedUntilRelease = false;
  private pistolRecoil = 0;
  private readonly pistolSeen = new Map<string, number>();
  private readonly pistolReloadSeen = new Map<string, number>();
  private readonly pistolMuzzle = new Vector3();
  private previousCrew = -1;
  private previousStation = "";
  private previousEquipment = "";
  private wasSliding = false;
  private patchAt = 0;
  private accumulator = 0;
  private holding: HoldTarget | null = null;
  /** Use target for a quick tap of the current hold, and when the hold began (ms). */
  private holdTap: UseTarget | null = null;
  private holdStartedAt = 0;
  private readonly throwEdge: OverboardThrow = { x: 0, z: 0, nx: 0, nz: 0 };
  private target: Target | null = null;
  private readonly motion = new MovementAudioTracker();
  private readonly heldAction = new ActionAudioTracker();
  private readonly motionBefore = newMotion();
  private readonly motionAfter = newMotion();
  private readonly cues: MotionCues = { steps: 0, strokes: 0, landing: 0 };
  private pendingSteps = 0;
  private pendingStrokes = 0;
  private pendingLanding = 0;
  private readonly audioPos: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly listenerForward: Vec3 = { x: 0, y: 0, z: 1 };
  private readonly listenerUp: Vec3 = { x: 0, y: 1, z: 0 };
  private readonly audioShips: ShipAudioState[] = [];
  private readonly shipAudioPool: ShipAudioState[] = [];
  private readonly shipCandidates: { slot: number; d: number }[] = [];
  private readonly ambience: AmbienceFrame = { underwater: false, shore: 0, ships: this.audioShips };
  private shoreAt = 0;
  private lastFrame = performance.now();
  private onlineView: OnlineView | null = null;
  private onlineAt = 0;
  private disposed = false;
  private leaving = false;
  private reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
  private lastDrownSound = 0;
  private lockFailed = false;
  private lastFlooding = 0;
  private lastDanger = 0;
  private lastHealth = Infinity;
  /** Cannonballs that already whistled past this listener. */
  private readonly whizzed = new Set<string>();
  private autoInput: AutoInput | null = null;
  private readonly movementContext: MovementContext = { carrying: false, sprintAllowed: true };
  private readonly fpsSamples: number[] = [];
  /** Server tick the current frame renders (interpolation time). */
  private lastRenderTick = 0;
  /** The room's weather this frame (`weatherAt` at `lastRenderTick`). */
  private readonly weatherSample = createWeatherSample();
  /** Test hook: fields that replace the room's weather (`overrideWeather`). */
  private weatherOverride: Partial<WeatherSample> | null = null;

  constructor(
    private readonly engine: Engine,
    private readonly room: GameRoom,
    private readonly net: Net,
    uiRoot: HTMLElement,
    private readonly onExit: (message?: string) => void,
  ) {
    const canvas = engine.getRenderingCanvas()!;
    this.royale = room.roomId === ROYALE_ROOM_ID;
    this.scene = new Scene(engine);
    this.scene.skipPointerMovePicking = true;
    this.env = new Environment(this.scene, settings.value.graphicsPreset, settings.value.contactShadowSamples);
    if (this.royale) {
      for (const layer of ["terrain", "landforms", "structures", "vegetation", "wildlife"] as const) this.env.setLayerVisible(layer, false);
      const ring = CreateCylinder("royale-ring", { diameter: 2, height: 40, tessellation: 128, cap: Mesh.NO_CAP, sideOrientation: Mesh.DOUBLESIDE }, this.scene);
      const ringMat = this.env.mats.color("royale-ring", "#ff5a3c", 0.6, 0.25);
      ringMat.backFaceCulling = false;
      ring.material = ringMat;
      ring.isPickable = false;
      ring.position.set(BATTLE_ROYALE.center.x, 10, BATTLE_ROYALE.center.z);
      ring.scaling.x = ring.scaling.z = BATTLE_ROYALE.ringStart;
      this.ringMesh = ring;
      const beacon = CreateCylinder("treasure-beacon", { diameter: 0.6, height: 60, tessellation: 12 }, this.scene);
      beacon.material = this.env.mats.color("treasure-beacon", "#ffd34d", 1, 0.35);
      beacon.isPickable = false;
      beacon.setEnabled(false);
      this.treasureBeacon = beacon;
    }
    const unsubscribeGraphics = settings.subscribe(s => {
      this.env.setGraphicsPreset(s.graphicsPreset);
      this.env.setContactShadowSamples(s.contactShadowSamples);
    });
    this.scene.onDisposeObservable.addOnce(unsubscribeGraphics);
    if (!this.royale) for (const mount of ISLAND_CANNONS) this.islandCannons.set(mount.id, new IslandCannonModel(this.scene, this.env, mount));
    for (let i = 0; i < 96; i++) {
      const ball = CreateSphere(`ball:${i}`, { diameter: CANNON.ballRadius * 2, segments: 6 }, this.scene);
      ball.material = this.env.mats.get("iron");
      sphereProjectUVs(ball, SURFACE_PROFILES.iron.metres);
      ball.isPickable = false;
      ball.setEnabled(false);
      this.balls.push(ball);
    }
    this.landingRing = CreateTorus("landingRing", { diameter: 3, thickness: 0.08, tessellation: 24 }, this.scene);
    const ringMaterial = new StandardMaterial("landingRingMaterial", this.scene);
    ringMaterial.disableLighting = true;
    this.landingRing.material = ringMaterial;
    this.landingRing.isPickable = false;
    this.landingRing.setEnabled(false);
    this.effects = new Effects(this.scene);
    this.camera = new UniversalCamera("cam", Vector3.Zero(), this.scene);
    this.camera.inputs.clear();
    this.camera.minZ = 0.05;
    this.camera.maxZ = 6000;
    this.camera.fovMode = UniversalCamera.FOVMODE_VERTICAL_FIXED;
    this.camera.fov = BASE_VERTICAL_FOV;
    this.env.attachCamera(this.camera);
    this.selfAvatar = new Avatar(this.scene, this.env, room.sessionId);
    this.selfAvatar.setVisible(false);
    this.input = new Input(canvas);
    this.hud = new Hud(uiRoot, {
      resume: () => void this.resume(),
      recover: () => {
        if (!this.flightActive) this.room.send(C2S.recover, { cancel: false });
        void this.resume();
      },
      abandon: () => this.requestAbandon(),
      leave: () => this.leave(),
      chooseCrew: (choice: CrewChoice) => this.room.send(C2S.crew, { choice }),
      releaseForDialog: () => this.releaseForDialog(),
      bot: (add) => this.room.send(C2S.bot, { add }),
      takeQuest: (kind) => {
        if (!this.flightActive) this.room.send(C2S.use, { target: `questBoard:${kind}` });
      },
      online: () => {
        this.hud.toggleOnline();
        if (this.onlineView && this.hud.onlineOpen) this.hud.setOnline(this.onlineView);
      },
    }, this.royale);
    matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", (e) => (this.reducedMotion = e.matches));

    this.input.onClear = () => { this.resetScope(); this.releaseBlock(); };
    this.input.onLockChange = (locked) => {
      if (!locked) this.releaseBlock();
      if (locked) {
        this.lockFailed = false;
        this.hud.setPaused(false, "");
        return;
      }
      this.stopHold();
      if (!this.leaving && !this.hud.dialogOpen && !this.hud.mapOpen) this.showPause();
    };
    canvas.addEventListener("click", () => {
      audio.unlock();
      if (!this.input.locked && !this.hud.dialogOpen) void this.resume();
    });
    this.hud.root.addEventListener("click", (e) => {
      if (this.lockFailed && e.target === this.hud.ref("pause")) void this.resume();
    });
    window.addEventListener("keydown", this.onEscape);
    window.addEventListener("storage", this.onLookSaved);
    // A resumed seat keeps its old look on the server; wear whatever is saved now.
    this.room.send(C2S.look, savedLookCode());
    document.addEventListener("pointerlockerror", () => {
      this.lockFailed = true;
      this.showPause("Click anywhere to resume.");
    });
    this.showPause("Click Resume (or anywhere) to capture the mouse and start playing.");

    this.bindRoom();
    if (new URLSearchParams(location.search).has("autotest")) this.exposeTestHooks();
    audio.setGameplayActive(true);
    engine.runRenderLoop(() => this.frame());
  }

  // ---------------------------------------------------------------------------
  // Networking

  private bindRoom(): void {
    const room = this.room;
    room.onStateChange(() => this.onPatch());
    room.onMessage(S2C.toast, (m: ToastMsg) => {
      this.hud.toast(m.kind, m.text);
      if (m.kind === "bad" && performance.now() - this.lastDanger > DANGER_GAP) {
        this.lastDanger = performance.now();
        audio.play("danger");
      }
    });
    room.onMessage(S2C.fx, (m: FxMsg) => this.onFx(m));
    room.onMessage(S2C.meleeResult, (m: MeleeResultMsg) => this.onMeleeResult(m));
    room.onMessage(S2C.attackAck, (m: AttackAckMsg) => this.onAttackAck(m));
    room.onMessage(S2C.pistolShot, (m: PistolShotMsg) => this.onPistolShot(m));
    room.onMessage(S2C.sale, (m: SaleMsg) => {
      this.hud.showSale(m);
      this.completeStep("sell");
      if (m.newTitle) audio.play("milestone");
    });
    room.onMessage(S2C.digResult, (m: DigResultMsg) => this.onDigResult(m));
    room.onMessage(S2C.islandRead, (m: IslandReadMsg) => this.hud.showIslandRead(m.id));
    room.onMessage(S2C.debug, (m: unknown) => {
      this.lastDebug = m;
    });
    room.onDrop(() => {
      this.hud.setBanner("conn", "⟳ Connection lost — reconnecting…");
      this.stopHold();
      this.input.clear();
      this.resetPistolFeedback();
      this.attackPrediction = 0;
      this.queuedSerial = 0;
    });
    room.onReconnect(() => {
      // The server issues a fresh reconnection token on every reconnect; the SDK
      // stores it only after onReconnect handlers return, so read it afterwards.
      queueMicrotask(() => this.net.remember(room));
      this.hud.setBanner("conn", null);
      this.hud.toast("good", "Connection restored.");
      this.predictor.resetSequence();
      this.attackRequestId = this.pendingAttackRequest = this.pendingQueueRequest = 0;
      this.queuedSerial = 0;
      this.meleeSeen.clear();
      this.input.clear();
      this.resetPistolFeedback();
      this.clock.reset();
    });
    room.onLeave((code) => {
      if (this.leaving) return;
      this.net.forget();
      this.dispose();
      this.onExit(code === 4002 ? "Disconnected: too many messages." : "Disconnected from the room. It may have expired.");
    });
  }

  lastDebug: unknown = null;

  private onPatch(): void {
    const s = this.room.state;
    const now = performance.now() / 1000;
    this.patchAt = now;
    this.clock.observe(s.tick, now);
    s.crews.forEach((crew) => {
      const ship = crew.ship;
      let client = this.ships.get(crew.slot);
      if (!client) {
        const tag = document.createElement("div");
        tag.className = "nametag";
        // A slot's crew name and colour never change while the crew exists.
        tag.textContent = crew.npc ? `Pirates: ${crew.name}` : crew.name;
        tag.style.color = crew.color;
        this.hud.root.append(tag);
        const model = new ShipModel(this.scene, this.env, crew.color);
        client = {
          model,
          buffer: new SnapshotBuffer(),
          pose: Object.assign(createPoseSample(), { x: ship.x, z: ship.z, yaw: ship.heading }),
          server: { x: ship.x, z: ship.z, heading: ship.heading },
          epoch: 0,
          generation: -1,
          prevLeaks: 0,
          bellRings: ship.bellRings,
          tag,
          ride: Matrix.Identity(),
          hull: { id: crew.slot, x: ship.x, z: ship.z, heading: ship.heading, speed: 0, bow: SHIP.draft, stern: SHIP.draft, water: model.waterPlane },
        };
        this.ships.set(crew.slot, client);
        this.renderPoses[crew.slot] = { ...client.server };
      }
      if (ship.generation !== client.generation) {
        client.generation = ship.generation;
        client.epoch++;
        client.model.resetPatches();
        if (this.climb.invalidate(crew.slot)) {
          this.ladderViewOffset = 0;
          this.selfAvatar.setVisible(false);
        }
        for (const remote of this.remotes.values()) remote.climb.invalidate(crew.slot);
      }
      const last = client.buffer.latest();
      if (last && Math.hypot(last.x - ship.x, last.z - ship.z) > 15) client.epoch++;
      client.buffer.push(s.tick, 0, ship.x, 0, ship.z, ship.heading, client.epoch);
      Object.assign(client.server, { x: ship.x, z: ship.z, heading: ship.heading });
      // Mirrors the server: a sinking hull is not walkable.
      this.poses[crew.slot] = ship.sinking ? null : client.server;
      const closed = client.prevLeaks & ~ship.leaks;
      for (let i = 0; i < SHIP.leakSlots; i++) if (closed & (1 << i)) client.model.markPatched(i);
      client.prevLeaks = ship.leaks;
      client.model.applyLook(ship.livery, ship.upgrades);
      client.model.setLanterns(ship.lanterns);
      client.model.updateRacks(ship.cannonballs, maxBalls(ship));
      if (ship.bellRings !== client.bellRings) {
        client.bellRings = ship.bellRings;
        client.model.ringBell();
      }
    });
    for (const [slot, client] of this.ships) {
      if (s.crews.has(String(slot))) continue;
      if (this.camera.parent === client.model.root) this.camera.parent = null;
      for (const remote of this.remotes.values()) {
        if (remote.avatar.root.parent === client.model.root) remote.avatar.root.setParent(null);
      }
      for (const chest of this.chests.values()) {
        if (chest.root.parent === client.model.root) chest.root.setParent(null);
      }
      client.model.dispose();
      client.tag.remove();
      this.ships.delete(slot);
      this.poses[slot] = null;
      this.renderPoses[slot] = null;
    }

    s.players.forEach((p, id) => {
      const previousReload = this.pistolReloadSeen.get(id);
      if (previousReload === 0 && p.pistolReloadRemaining > 0 && p.connected && !p.dead) {
        const ship = this.ships.get(frameSlot(p.frame))?.server;
        if (ship) localToWorld(ship, p.x, p.y, p.z, this.audioPos);
        else { this.audioPos.x = p.x; this.audioPos.y = p.y; this.audioPos.z = p.z; }
        this.audioPos.y += bodyEyeHeight(p);
        audio.play("pistolReload", { position: this.audioPos });
      }
      this.pistolReloadSeen.set(id, p.pistolReloadRemaining);
      if (id === this.room.sessionId) {
        if (p.look !== this.selfLook) {
          this.selfAvatar = this.relook(this.selfAvatar, id, p.look);
          this.selfLook = p.look;
        }
        this.reconcileSelf(p);
        return;
      }
      let r = this.remotes.get(id);
      if (!r) {
        const tag = document.createElement("div");
        tag.className = "nametag";
        this.hud.root.append(tag);
        r = {
          avatar: new Avatar(this.scene, this.env, id, decodeLook(p.look)),
          look: p.look,
          tag,
          buffer: new SnapshotBuffer(),
          epoch: 0,
          last: { tick: s.tick, frame: p.frame, mode: p.mode, launchSerial: p.launchSerial, x: p.x, y: p.y, z: p.z },
          sample: createPoseSample(),
          animator: new CharacterAnimator(),
          pose: createCharacterPose(),
          locomotion: { distance: 0, discontinuous: true, frameToWorld: null },
          gaitFrame: new Matrix(),
          prevX: 0,
          prevZ: 0,
          prevFrame: -1,
          prevEpoch: -1,
          prevShipEpoch: -1,
          climb: new ClimbPresentation(),
          attackSerial: p.attackSerial,
          scopeBlend: 0, recoil: 0, parryRaise: 0, hitFlinch: -1, hitStep: 0, cannonRecoil: 0,
          pistolAimBlend: 0, pistolRecoil: 0,
          motion: new MovementAudioTracker(),
          heldAction: new ActionAudioTracker(),
          motionPrev: newMotion(),
          motionNow: newMotion(),
        };
        this.remotes.set(id, r);
      }
      if (r.look !== p.look) {
        r.avatar = this.relook(r.avatar, id, p.look);
        r.look = p.look;
      }
      const sameFlight = r.last.mode === MODE_LAUNCH && p.mode === MODE_LAUNCH && r.last.launchSerial === p.launchSerial;
      const elapsedSeconds = Math.max(0, s.tick - r.last.tick) * DT;
      const teleportDistance = sameFlight ? (SELF_CANNON.speed + MAX_SAIL_SPEED) * elapsedSeconds + 2 : 6;
      const jump = r.last.frame === p.frame && Math.hypot(r.last.x - p.x, r.last.z - p.z) > teleportDistance;
      if (jump) r.epoch++;
      r.last.tick = s.tick;
      r.last.frame = p.frame;
      r.last.mode = p.mode;
      r.last.launchSerial = p.launchSerial;
      r.last.x = p.x;
      r.last.y = p.y;
      r.last.z = p.z;
      r.buffer.push(s.tick, p.frame, p.x, p.y, p.z, p.yaw, r.epoch, p);
    });
    for (const [id, r] of this.remotes) {
      if (!s.players.has(id)) {
        r.avatar.dispose();
        r.tag.remove();
        this.remotes.delete(id);
        this.pistolSeen.delete(id);
        this.pistolReloadSeen.delete(id);
      }
    }
    const me = this.me();
    const ownSlot = me?.crew ?? -1;
    const queued = me?.queued ?? false;
    if (this.royale && queued && !this.wasQueued) this.hud.closeCrewPicker();
    this.wasQueued = queued;
    const phase = s.royale.phase;
    const autoPick = !this.royale || ((phase === "waiting" || phase === "countdown") && !queued);
    const crews = new Map<number, CrewView>();
    const leaderboard: OnlineView["crews"][number][] = [];
    s.crews.forEach((c) => {
      if (c.npc) return;
      crews.set(c.slot, { slot: c.slot, name: c.name, color: c.color, members: 0, humans: 0, memberNames: [], gold: c.gold });
      leaderboard.push({ slot: c.slot, name: c.name, color: c.color, gold: c.gold, quests: c.questsCompleted });
    });
    const crew: CrewEntry[] = [];
    const roster: (OnlineView["players"][number] & { slot: number })[] = [];
    s.players.forEach((p, id) => {
      const entry = crews.get(p.crew);
      if (entry) {
        entry.members++;
        if (!p.bot) entry.humans++;
        (entry.memberNames as string[]).push(p.name + (p.bot ? " (bot)" : ""));
      }
      if (p.crew === ownSlot) crew.push({ id, name: p.name, bot: p.bot, connected: p.connected, self: id === this.room.sessionId, dead: p.dead, station: p.station, cannonLoaded: p.cannonLoaded, mode: p.mode });
      if (!p.bot || entry) roster.push({ id, name: p.name, crewName: entry?.name ?? "", bot: p.bot, connected: p.connected, slot: entry ? p.crew : CREW.maxCrews });
    });
    leaderboard.sort((a, b) => b.gold - a.gold || b.quests - a.quests || a.slot - b.slot);
    roster.sort((a, b) => a.slot - b.slot || Number(a.bot) - Number(b.bot));
    this.onlineView = { population: getSeaStatus(s), crews: leaderboard, players: roster };
    this.hud.setPopulation(this.onlineView.population);
    this.hud.setCrews(Array.from(crews.values()), ownSlot, autoPick);
    this.hud.setCrew(crew);
  }

  private me(): PlayerState | undefined {
    return this.room.state.players.get(this.room.sessionId);
  }

  private get flightActive(): boolean {
    return this.predictor.flightActive || this.me()?.mode === MODE_LAUNCH;
  }

  private ownCrew(): CrewState | undefined {
    return this.room.state.crews.get(String(this.me()?.crew ?? -1));
  }

  private reconcileSelf(p: PlayerState): void {
    if (this.observedLaunchSerial !== null && p.launchSerial !== this.observedLaunchSerial) {
      this.cannonRecoil = this.reducedMotion ? 0 : 1;
    }
    this.observedLaunchSerial = p.launchSerial;
    if (p.crew !== this.previousCrew || p.station !== this.previousStation || p.equipment !== this.previousEquipment ||
      p.dead || !p.connected || p.crew < 0) this.resetPistolAim();
    this.previousCrew = p.crew;
    this.previousStation = p.station;
    this.previousEquipment = p.equipment;
    if (p.mode === MODE_LAUNCH) {
      this.attackPrediction = 0;
      this.pendingAttackRequest = this.pendingQueueRequest = this.queuedSerial = 0;
      this.shovelSwing = 0;
      this.stopHold();
      this.releaseBlock();
      this.resetScope();
    }
    if (p.dead || !p.connected) this.input.clear();
    if (p.dead || p.station !== "" || p.crew < 0 || !p.connected) {
      this.attackPrediction = 0;
      const frameBefore = this.predictor.body.frame;
      this.predictor.follow(p);
      if (frameBefore !== p.frame) this.rebaseLook(frameBefore, p.frame);
      return;
    }
    const frameBefore = this.predictor.initialised ? this.predictor.body.frame : p.frame;
    const changed = this.predictor.reconcile(p, this.poses, this.movementPermissions(p));
    if (changed) {
      if (frameBefore !== this.predictor.body.frame) this.rebaseLook(frameBefore, this.predictor.body.frame);
      else this.input.yaw = this.predictor.body.yaw;
    }
  }

  /** Keeps the view direction steady in world space when the body frame flips. */
  private rebaseLook(from: number, to: number): void {
    if (from === to) return;
    this.input.yaw = wrapAngle(this.input.yaw + (this.renderPoses[frameSlot(from)]?.heading ?? 0) - (this.renderPoses[frameSlot(to)]?.heading ?? 0));
  }

  /** Predict against the same closed gates and intact crates the server collides with. */
  private syncWorldColliders(): void {
    const s = this.room.state;
    let key = "";
    for (let i = 0; i < s.gates.length; i++) key += s.gates[i] ? "1" : "0";
    key += ":";
    for (let i = 0; i < s.crates.length; i++) key += (s.crates[i] ?? 0) > 0 ? "1" : "0";
    if (key === this.colliderKey) return;
    this.colliderKey = key;
    this.predictor.setWorldColliders(worldColliders(s.gates, s.crates));
  }

  /** Gates, levers and crates follow replicated state; telescopes follow their observer's look. */
  private updateFixtures(dt: number, me: PlayerState | undefined): void {
    for (const observer of this.telescopeObservers) observer.active = false;
    this.room.state.players.forEach((p) => {
      if (!p.station.startsWith("telescope:") || p.dead) return;
      const observer = this.telescopeObservers[Number(p.station.slice(10))];
      if (!observer) return;
      // The local observer's look is predicted; remote observers use their replicated look.
      observer.active = true;
      observer.yaw = p === me ? this.input.yaw : p.yaw;
      observer.pitch = p === me ? this.input.pitch : p.pitch;
    });
    const local = me?.station.startsWith("telescope:") ? Number(me.station.slice(10)) : -1;
    this.env.fixtures.update(dt, this.room.state, this.telescopeObservers, local, this.reducedMotion);
  }

  // ---------------------------------------------------------------------------
  // Frame

  private frame(): void {
    if (this.disposed) return;
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    this.fpsSamples.push(dt);
    if (this.fpsSamples.length > 120) this.fpsSamples.shift();
    const me = this.me();
    this.input.overlay = this.hud.mapOpen || this.hud.dialogOpen;
    if (me?.cannonLoaded) {
      this.input.yaw = me.yaw;
      this.input.pitch = me.pitch;
    }

    this.handlePresses(me);
    this.syncWorldColliders();

    // Fixed-rate input + prediction.
    this.accumulator += dt;
    let steps = 0;
    while (this.accumulator >= DT && steps < 4) {
      this.accumulator -= DT;
      steps++;
      this.fixedStep(me);
    }
    if (steps === 4) this.accumulator = 0;
    this.predictor.decay(dt);

    this.updateShip(dt, now / 1000);
    this.updateCamera(dt, me);
    this.updateRemotes(dt);
    this.updateChests(dt);
    if (this.royale) this.updateRoyaleWorld(dt);
    this.updateFixtures(dt, me);
    this.updateSelfPresentation(dt, me);
    this.updateBalls(me);
    this.updateTarget(me);
    this.updateHud(me);
    this.env.setLanternState(this.room.state.lantern);
    this.env.update(dt, this.camera, this.reducedMotion);
    this.updateAudio(dt, me);
    this.effects.update(dt);
    this.scene.render();
  }

  private fixedStep(me: PlayerState | undefined): void {
    if (!me || me.crew < 0) return;
    const auto = this.input.active ? this.autoInput : null;
    const mx = auto ? auto.mx : this.input.axis("KeyA", "KeyD");
    const mz = me.station === "wheel" ? 0 : auto ? auto.mz : this.input.axis("KeyS", "KeyW");
    const jump = auto ? auto.jump : this.input.isDown("Space");
    const dive = auto ? auto.dive : this.input.isDown("KeyC");
    const flight = this.flightActive;
    const msg: InputMsg = {
      seq: ++this.predictor.seq,
      mx: flight ? 0 : mx,
      mz: flight ? 0 : mz,
      yaw: this.input.yaw,
      pitch: this.input.pitch,
      yawFrame: this.predictor.body.frame,
      jump: !flight && jump,
      dive: !flight && dive,
      sprint: !flight && (auto ? auto.sprint : this.input.isDown("ShiftLeft") || this.input.isDown("ShiftRight")),
      crouch: !flight && (auto ? auto.crouch : this.input.isDown("KeyC")),
      scope: this.scopeIntent(me) || this.pistolAimIntent(me),
    };
    this.room.send(C2S.input, msg);
    if (me.dead || !me.connected || me.station !== "" || !this.predictor.initialised || this.room.state.paused) {
      this.motion.reset();
      return;
    }
    this.captureMotion(this.motionBefore, me);
    const frameBefore = this.predictor.body.frame;
    const ev = this.predictor.step(msg, this.poses, this.movementPermissions(me));
    if (ev.frameChanged || frameBefore !== this.predictor.body.frame) this.input.yaw = this.predictor.body.yaw;
    // Movement sound comes from accepted simulation steps only, never from reconciliation snaps.
    this.captureMotion(this.motionAfter, me);
    this.motion.advance(this.motionBefore, this.motionAfter, DT, this.cues);
    this.pendingSteps = Math.min(MAX_CUES, this.pendingSteps + this.cues.steps);
    this.pendingStrokes = Math.min(MAX_CUES, this.pendingStrokes + this.cues.strokes);
    this.pendingLanding = Math.max(this.pendingLanding, this.cues.landing);
    if ((mx !== 0 || mz !== 0) && this.input.takeLook() > 50) this.completeStep("move");
  }

  private movementPermissions(me: PlayerState): MovementContext {
    this.movementContext.carrying = carriesLoad(me);
    this.movementContext.sprintAllowed = me.attackRemaining <= 0 && me.staggerRemaining <= 0 && this.attackPrediction <= 0 &&
      me.pistolRecoveryRemaining <= 0 && me.pistolReloadRemaining <= 0 &&
      !me.blocking && !this.blockSent && !me.station && !this.movementContext.carrying && !me.action &&
      me.recoverIn <= 0 && !this.flightActive && this.predictor.body.mode !== MODE_SWIM;
    return this.movementContext;
  }

  private scopeIntent(me: PlayerState): boolean {
    const b = this.predictor.body;
    const sprint = this.forwardSprintIntent();
    return this.input.active && (this.autoInput ? this.autoInput.scope : this.input.isSecondaryHeld()) && !this.scopeSuppressedUntilRelease &&
      me.connected && !me.dead && !this.flightActive && me.crew >= 0 && me.equipment === "spyglass" &&
      !me.station && !me.carrying && !me.action && me.recoverIn <= 0 &&
      me.attackRemaining <= 0 && me.staggerRemaining <= 0 && this.attackPrediction <= 0 &&
      b.mode !== MODE_SWIM && b.slideRemaining <= 0 && !b.sprinting && !sprint;
  }

  private forwardSprintIntent(): boolean {
    const b = this.predictor.body;
    const sprint = this.autoInput ? this.autoInput.sprint : this.input.isDown("ShiftLeft") || this.input.isDown("ShiftRight");
    const forward = this.autoInput ? this.autoInput.mz : this.input.axis("KeyS", "KeyW");
    return sprint && forward > 0 && b.grounded && !b.crouched;
  }

  private canPistol(me: PlayerState): boolean {
    const b = this.predictor.body;
    return me.connected && !me.dead && !this.flightActive && me.crew >= 0 && me.equipment === "pistol" &&
      b.mode !== MODE_SWIM && !b.sprinting && b.slideRemaining <= 0 &&
      !me.station && !me.carrying && !me.action && !this.holding && me.recoverIn <= 0 &&
      me.attackRemaining <= 0 && me.staggerRemaining <= 0 && this.attackPrediction <= 0;
  }

  private pistolAimIntent(me: PlayerState): boolean {
    return this.input.active && (this.autoInput ? this.autoInput.scope : this.input.isSecondaryHeld()) &&
      !this.pistolAimSuppressedUntilRelease && this.canPistol(me) &&
      me.pistolReloadRemaining <= 0 && !this.forwardSprintIntent();
  }

  private resetPistolAim(): void {
    this.pistolAimBlend = 0;
    this.pistolAimSuppressedUntilRelease = true;
    if (this.scopeBlend === 0) {
      this.magnification = 1;
      this.input.setLookScale(1);
      this.camera.fov = BASE_VERTICAL_FOV;
    }
  }

  private resetPistolFeedback(): void {
    this.pistolSeen.clear();
    this.pistolReloadSeen.clear();
    this.pistolRecoil = 0;
    for (const remote of this.remotes.values()) remote.pistolAimBlend = remote.pistolRecoil = 0;
  }

  private resetScope(): void {
    this.resetPistolAim();
    this.scopeBlend = 0;
    this.magnification = 1;
    this.scopeSuppressedUntilRelease = true;
    this.input.setLookScale(1);
    this.camera.fov = BASE_VERTICAL_FOV;
    this.hud.setScope(0, SPYGLASS.magnification);
    this.target = null;
  }

  private handlePresses(me: PlayerState | undefined): void {
    if (!this.input.isSecondaryHeld() && !this.autoInput?.scope) {
      this.scopeSuppressedUntilRelease = false;
      this.pistolAimSuppressedUntilRelease = false;
    }
    const pressed = this.input.takePressed();
    if (this.input.takeInteractReleased()) this.stopHold();
    for (const key of pressed) {
      if (key === "players") {
        this.hud.toggleOnline();
        if (this.onlineView && this.hud.onlineOpen) this.hud.setOnline(this.onlineView);
        this.input.overlay = this.hud.mapOpen || this.hud.dialogOpen;
        continue;
      }
      if (key === "map") {
        if (this.hud.paused || this.hud.dialogOpen) continue;
        const open = !this.hud.mapOpen;
        this.hud.setMapOpen(open);
        this.input.overlay = this.hud.mapOpen || this.hud.dialogOpen;
        if (!open) void this.resume();
        audio.play("click");
        continue;
      }
      if (key === "toggleView") {
        this.view = this.view === "first" ? "third" : "first";
        continue;
      }
      if (this.spectating && key === "interact") {
        this.cycleSpectate();
        continue;
      }
      if (this.spectating && key === "crewMenu") {
        this.hud.openCrewPicker();
        continue;
      }
      if (!me || me.dead || !this.input.active) continue;
      switch (key) {
        case "equipSword":
        case "equipShovel":
        case "equipSpyglass":
        case "equipPistol":
          this.pistolAimSuppressedUntilRelease = true;
          this.room.send(C2S.equip, { equipment: key === "equipSword" ? "sword" : key === "equipShovel" ? "shovel" : key === "equipPistol" ? "pistol" : "spyglass" });
          break;
        case "loadSelf":
          if (!this.flightActive && !me.cannonLoaded && this.isCannonStation(me.station)) this.room.send(C2S.loadSelf, {});
          break;
        case "reload":
          if (this.canPistol(me) && me.pistolRounds < PISTOL.capacity && me.pistolReserve > 0 &&
            me.pistolRecoveryRemaining <= 0 && me.pistolReloadRemaining <= 0) {
            this.pistolAimSuppressedUntilRelease = true;
            this.room.send(C2S.reload, {});
          }
          break;
        case "interact":
          this.pistolAimSuppressedUntilRelease = true;
          if (this.scopeBlend > 0 || me.scopeRaised) {
            this.scopeSuppressedUntilRelease = true;
            this.target = null;
            break;
          }
          this.interact(me);
          break;
        case "drop":
          if (me.carrying) this.room.send(C2S.drop, {});
          break;
        case "sailUp":
        case "sailDown":
          if (me.station === "wheel") this.room.send(C2S.sail, { delta: key === "sailUp" ? 1 : -1 });
          break;
        case "voteYes":
        case "voteNo":
          if (this.ownCrew()?.vote.active) this.room.send(C2S.vote, { yes: key === "voteYes" });
          break;
      }
    }
    const blocking = !!me && this.canMelee(me) && me.attackRemaining <= 0 && this.attackPrediction <= 0 && this.input.isSecondaryHeld();
    if (blocking !== this.blockSent) {
      this.blockSent = blocking;
      this.room.send(C2S.block, { active: blocking });
    }
    if (this.input.takePrimary() && me && !me.dead && !this.flightActive) {
      const bucket = this.bucketAction(me);
      if (this.isCannonStation(me.station)) this.room.send(C2S.fire, {});
      else if (bucket === "fill") this.room.send(C2S.fill, {});
      else if (bucket === "throw") this.room.send(C2S.throw, {});
      else if (this.canPistol(me) && me.pistolRecoveryRemaining <= 0 && me.pistolReloadRemaining <= 0) {
        if (me.pistolRounds > 0) this.room.send(C2S.shoot, {
          yaw: this.input.yaw, pitch: this.input.pitch, yawFrame: this.predictor.body.frame,
        });
        else audio.play("pistolEmpty");
      }
      else if (this.canMelee(me) && !blocking && !me.blocking) {
        const remaining = Math.max(0, me.attackRemaining - this.patchAge());
        const queue = me.attackRemaining > 0 && remaining <= MELEE.queueWindow &&
          me.attackStep < 2 && this.strikeHitSerial === me.attackSerial &&
          this.pendingQueueRequest === 0 && this.queuedSerial === 0;
        if (queue || (me.attackRemaining <= 0 && this.attackPrediction <= 0 && !this.pendingAttackRequest)) {
          const requestId = this.attackRequestId = (this.attackRequestId + 1) >>> 0;
          this.room.send(C2S.attack, { requestId });
          if (queue) this.pendingQueueRequest = requestId;
          else {
            this.pendingAttackRequest = requestId;
            const timing = MELEE.combo[0];
            this.attackPrediction = timing.windup + timing.active + timing.recovery;
            this.predictedSerial = (me.attackSerial + 1) >>> 0;
            this.attackClock = 0;
            this.attackClockSerial = this.predictedSerial;
            this.displayAttackStep = 0;
            this.displayAttackDuration = this.attackPrediction;
            this.playedAttackSerial = this.predictedSerial;
            audio.play("swordSwing", { position: this.swingPosition() });
          }
        }
      } else if (this.canDig(me)) {
        this.room.send(C2S.dig, {});
        this.shovelSwing = 1;
      }
    }
  }

  private canMelee(me: PlayerState): boolean {
    return me.connected && !me.dead && !this.flightActive && me.crew >= 0 && me.equipment === "sword" &&
      this.predictor.body.mode !== MODE_SWIM && !this.predictor.body.sprinting && this.predictor.body.slideRemaining <= 0 &&
      !me.station && !me.carrying && !me.action && me.recoverIn <= 0 && me.staggerRemaining <= 0 && me.pistolRecoveryRemaining <= 0;
  }

  private releaseBlock(): void {
    if (!this.blockSent) return;
    this.blockSent = false;
    this.room.send(C2S.block, { active: false });
  }

  private canDig(me: PlayerState): boolean {
    const b = this.predictor.body;
    // Standing on island ground (not the pier, not the deck, not wading).
    return (
      me.crew >= 0 &&
      me.equipment === "shovel" &&
      me.connected && !me.dead && !this.flightActive && !me.action && me.recoverIn <= 0 &&
      b.frame === FRAME_WORLD &&
      b.mode === MODE_WALK &&
      b.grounded &&
      !me.carrying &&
      me.station === "" &&
      b.y > 0 &&
      b.y - terrainHeight(b.x, b.z) < 0.3 &&
      landmassAt(b.x, b.z) !== undefined
    );
  }

  private interact(me: PlayerState): void {
    if (this.flightActive) return;
    // A capstan bar is held, not toggled: releasing E lets go of it (stopHold).
    if (me.station !== "" && capstanBar(me.station) < 0) {
      this.room.send(C2S.use, { target: me.station });
      return;
    }
    const t = this.target;
    if (!t) return;
    audio.play("click");
    if (t.hold) {
      this.holding = t.id as HoldTarget;
      this.holdTap = t.tap ?? null;
      this.holdStartedAt = performance.now();
      this.room.send(C2S.hold, { target: this.holding });
      return;
    }
    if (t.id === "mapTable") {
      this.hud.setMapOpen(true);
      this.input.overlay = true;
      return;
    }
    if (t.id === "questBoard") {
      if (this.ownCrew()?.quest.status !== "none") this.openQuestDialog();
      else this.hud.openBoard();
      return;
    }
    this.room.send(C2S.use, { target: t.id });
    if (t.id === "wheel") this.completeStep("wheel");
  }

  private stopHold(): void {
    if (!this.holding) return;
    this.holding = null;
    this.room.send(C2S.hold, { target: null });
    // A quick press on a tap-or-hold target (a damaged helm) uses it instead of starting the repair.
    const tap = this.holdTap;
    this.holdTap = null;
    if (tap && performance.now() - this.holdStartedAt < TAP_MS) {
      this.room.send(C2S.use, { target: tap });
      if (tap === "wheel") this.completeStep("wheel");
    }
  }

  private openQuestDialog(): void {
    const q = this.ownCrew()?.quest;
    if (!q || !isIslandId(q.island)) return;
    const v = this.ownCrew()?.vote;
    this.hud.openQuestDialog(q.island, q.spot, v?.active ? "An abandon vote is in progress." : null);
  }

  /** Frees the mouse for a dialog without showing the pause menu. */
  private releaseForDialog(): void {
    this.stopHold();
    this.input.overlay = this.hud.mapOpen || this.hud.dialogOpen;
    this.releaseBlock();
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private requestAbandon(): void {
    const q = this.ownCrew()?.quest;
    if (!q || q.status === "none") return;
    let connected = 0;
    this.room.state.players.forEach((p) => {
      if (!p.bot && p.connected && p.crew === this.me()?.crew) connected++;
    });
    const v = this.ownCrew()?.vote;
    if (!v) return;
    if (v.active) {
      this.hud.toast("info", "A vote is already in progress.");
      return;
    }
    if (v.cooldown > 0) {
      this.hud.toast("info", `Please wait ${Math.ceil(v.cooldown)}s before starting another vote.`);
      return;
    }
    this.hud.setPaused(false, "");
    this.hud.closeQuestDialog();
    this.releaseForDialog();
    const text = connected <= 1 ? "Abandon this quest? Its treasure will be lost." : "Start a vote to abandon this quest? Your crewmate must agree.";
    this.hud.confirm(text, () => {
      this.room.send(C2S.abandon, {});
      this.showPause();
    });
  }

  private async resume(): Promise<void> {
    audio.unlock();
    this.hud.closeDialogs();
    if (this.hud.dialogOpen) return;
    const ok = await this.input.lock();
    if (!ok) {
      this.lockFailed = true;
      this.showPause("The browser needs a moment. Click anywhere to resume.");
    }
  }

  private showPause(note = "Paused. Click Resume to continue."): void {
    if (this.input.locked || this.input.forceActive) return;
    const q = this.ownCrew()?.quest;
    const me = this.me();
    this.hud.setMenuState(!!me && !me.dead && !this.flightActive && me.recoverIn <= 0, !!q && q.status !== "none");
    this.hud.setMapOpen(false);
    this.hud.setPaused(true, note);
  }

  private leave(): void {
    this.leaving = true;
    this.net.forget();
    void this.room.leave(true).finally(() => {
      this.dispose();
      this.onExit();
    });
  }

  // ---------------------------------------------------------------------------
  // Rendering

  private updateShip(dt: number, now: number): void {
    this.lastRenderTick = this.clock.renderTick(now);
    // Before any hull samples the sea, so ships ride the waves this frame renders.
    const weather = weatherAt(this.room.state.weatherSeed, this.lastRenderTick * DT, this.weatherSample);
    if (this.weatherOverride) {
      Object.assign(weather, this.weatherOverride);
      if (this.weatherOverride.storm === undefined) weather.storm = smoothstep(WEATHER.stormStart, WEATHER.stormFull, weather.windSpeed);
    }
    this.env.setWeather(weather);
    this.hullSources.length = 0;
    for (const [slot, client] of this.ships) {
      const crew = this.room.state.crews.get(String(slot));
      if (!crew) continue;
      const s = crew.ship;
      if (client.buffer.sample(this.lastRenderTick, client.pose)) {
        client.model.root.position.x = client.pose.x;
        client.model.root.position.z = client.pose.z;
        client.model.root.rotation.y = client.pose.yaw;
        const pose = this.renderPoses[slot]!;
        pose.x = client.pose.x;
        pose.z = client.pose.z;
        pose.heading = client.pose.yaw;
      }
      // The water's pose under the hull, then the hull's own inertial response to it.
      this.env.shipMotion(client.pose.x, client.pose.z, client.pose.yaw, dt, this.reducedMotion, client.model.waterPlane);
      const motion = client.model.ride(dt, this.env.waves.hull, this.reducedMotion);
      // ride = flat⁻¹ · riding, both yawed about the same pose, so passengers keep the physical deck frame.
      Matrix.RotationYToRef(client.pose.yaw, this.rideFlat);
      this.rideFlat.setTranslationFromFloats(client.pose.x, 0, client.pose.z).invertToRef(this.rideFlat);
      Matrix.RotationYawPitchRollToRef(client.pose.yaw, motion.pitch, motion.roll, this.rideHull);
      this.rideHull.setTranslationFromFloats(client.pose.x, motion.heave, client.pose.z);
      this.rideFlat.multiplyToRef(this.rideHull, client.ride);
      const sinkProgress = s.sinking ? Math.min(1, 1 - s.sinkIn / SHIP.sinkSeconds) : 0;
      const visible = slot === this.me()?.crew || slot === frameSlot(this.predictor.body.frame) ||
        Math.hypot(client.pose.x - this.camera.position.x, client.pose.z - this.camera.position.z) <= 1800;
      client.model.root.setEnabled(visible);
      if (!visible) { client.tag.hidden = true; continue; }
      client.model.setDamage(s);
      client.model.update(dt, s.sail, s.rudder, s.leaks, s.flooding, sinkProgress, s.speed, this.reducedMotion);
      client.model.updateAnchor(dt, s.anchorDepth, s.anchorFalling);
      const hull = client.hull;
      hull.x = client.pose.x; hull.z = client.pose.z; hull.heading = client.pose.yaw; hull.speed = s.sinking ? 0 : s.speed;
      hull.bow = client.model.immersion.bow; hull.stern = client.model.immersion.stern;
      this.hullSources.push(hull);
      client.model.updateCannons(s.cannons, this.room.state.players);
      const me = this.me();
      this.room.state.players.forEach((p) => {
        if (p.station !== "nestScope" || p.dead || frameSlot(p.frame) !== slot) return;
        // The local lookout's view is predicted; remote lookouts use their replicated ship-local look.
        client.model.updateNestScope(p === me ? this.input.yaw : p.yaw, p === me ? this.input.pitch : p.pitch);
      });
      this.tagWorld.set(client.pose.x, 9, client.pose.z);
      this.viewport.width = this.engine.getRenderWidth();
      this.viewport.height = this.engine.getRenderHeight();
      Vector3.ProjectToRef(this.tagWorld, Matrix.IdentityReadOnly, this.scene.getTransformMatrix(), this.viewport, this.tagScreen);
      const hidden = slot === this.me()?.crew || Vector3.Distance(this.camera.globalPosition, this.tagWorld) > 300 || this.tagScreen.z <= 0 || this.tagScreen.z >= 1;
      if (client.tag.hidden !== hidden) client.tag.hidden = hidden;
      if (hidden) continue;
      const scale = this.engine.getHardwareScalingLevel();
      client.tag.style.transform = `translate(${this.tagScreen.x * scale}px, ${this.tagScreen.y * scale}px) translate(-50%, -100%)`;
    }
    this.env.setHullSources(this.hullSources);
    for (const [id, model] of this.islandCannons) {
      const owner = this.room.state.islandCannons.get(id)?.owner;
      model.update(owner ? this.room.state.players.get(owner) : undefined);
    }
  }

  private updateBalls(me: PlayerState | undefined): void {
    let i = 0;
    const cam = this.camera.globalPosition, ownCrew = me?.crew ?? -1;
    this.room.state.balls.forEach((ball, id) => {
      const mesh = this.balls[i++];
      const age = (this.lastRenderTick - ball.tick) * DT;
      mesh?.setEnabled(age >= 0);
      if (age < 0) return;
      ballPositionAt(ball, age, tmp);
      mesh?.position.set(tmp.x, tmp.y, tmp.z);
      if (ball.ship !== ownCrew && !this.whizzed.has(id) && Math.hypot(tmp.x - cam.x, tmp.y - cam.y, tmp.z - cam.z) < WHIZ_RADIUS) {
        this.whizzed.add(id);
        audio.play("ballWhiz", { position: tmp });
      }
    });
    for (const id of this.whizzed) if (!this.room.state.balls.has(id)) this.whizzed.delete(id);
    for (; i < this.balls.length; i++) this.balls[i]!.setEnabled(false);
    this.landingRing.setEnabled(false);
    if (!me || this.flightActive || !this.isCannonStation(me.station)) {
      this.previewCache.station = "";
      return;
    }
    const shoreId = me.station.startsWith("islandCannon:") ? me.station.slice(13) : "";
    const shore = isIslandId(shoreId) ? getIslandCannon(shoreId) : undefined;
    const slot = frameSlot(me.frame);
    const pose = this.renderPoses[slot];
    const crew = this.room.state.crews.get(String(slot));
    if (!shore && (!pose || !crew)) return;
    const playerShot = !!shore || me.cannonLoaded;
    const yaw = me.cannonLoaded ? me.yaw : this.input.yaw;
    const pitch = me.cannonLoaded ? me.pitch : this.input.pitch;
    const x = shore?.x ?? pose!.x, z = shore?.z ?? pose!.z;
    const heading = shore?.heading ?? pose!.heading;
    const speed = shore ? 0 : crew!.ship.speed;
    const cache = this.previewCache;
    const changed = cache.station !== me.station || cache.loaded !== me.cannonLoaded ||
      cache.yaw !== yaw || cache.pitch !== pitch || cache.x !== x || cache.z !== z ||
      cache.heading !== heading || cache.speed !== speed;
    if (changed) {
      cache.station = me.station; cache.loaded = me.cannonLoaded;
      cache.yaw = yaw; cache.pitch = pitch; cache.x = x; cache.z = z;
      cache.heading = heading; cache.speed = speed; cache.valid = false;
      this.shotShip.x = x; this.shotShip.z = z; this.shotShip.heading = heading; this.shotShip.speed = speed;
      if (playerShot) {
        if (shore) {
          islandCannonAim(shore.id, yaw, pitch, this.previewAim);
          Object.assign(this.previewPivot, shore.point);
        } else {
          const index = Number(me.station.slice(7));
          cannonAim(index, yaw, pitch, this.previewAim);
          const mount = SHIP_POINTS.cannons[index]!;
          localToWorld(pose!, mount.point.x, SHIP.deckHeight + CANNON.pivotHeight, mount.point.z, this.previewPivot);
          this.previewAim.yaw += heading;
        }
        this.previewInherited.x = Math.sin(heading) * speed;
        this.previewInherited.y = 0;
        this.previewInherited.z = Math.cos(heading) * speed;
        playerCannonShot(this.previewPivot, this.previewAim.yaw, this.previewAim.elevation,
          this.previewInherited, this.shotPosition, this.shotVelocity);
      } else {
        cannonShot(this.shotShip, Number(me.station.slice(7)), yaw, pitch, this.shotPosition, this.shotVelocity);
      }
      const ball = this.previewBall;
      ball.x = this.shotPosition.x; ball.y = this.shotPosition.y; ball.z = this.shotPosition.z;
      ball.vx = this.shotVelocity.x; ball.vy = this.shotVelocity.y; ball.vz = this.shotVelocity.z;
      const interval = playerShot ? 3 * DT : 0.05;
      for (let t = interval; t <= CANNON.lifetime; t += interval) {
        if (playerShot) playerLaunchPositionAt(ball, Math.round(t / DT), tmp);
        else ballPositionAt(ball, t, tmp);
        const terrain = terrainHeight(tmp.x, tmp.z);
        const support = playerShot ? (terrain < PLAYER.swimDepth ? PLAYER.surfaceFeetY : terrain) : Math.max(0, terrain);
        if (tmp.y > support) continue;
        this.landingRing.position.set(tmp.x, Math.max(0, terrain) + 0.06, tmp.z);
        cache.valid = true;
        break;
      }
    }
    const material = this.landingRing.material;
    const color = this.ownCrew()?.color ?? crew?.color ?? "#ffffff";
    if (material instanceof StandardMaterial && color !== this.ringColor) {
      material.emissiveColor = Color3.FromHexString(color);
      this.ringColor = color;
    }
    this.landingRing.setEnabled(cache.valid);
  }

  private updateRemotes(dt: number): void {
    const tick = this.lastRenderTick;
    const players = this.room.state.players;
    const patchAge = this.patchAge();
    const me = this.me();
    const localBarrel = me ? this.stationBarrel(me) : null;
    for (const [id, r] of this.remotes) {
      const p = players.get(id);
      const sample = r.sample;
      const previousSerial = sample.launchSerial;
      const previousMode = sample.mode;
      const previousStation = sample.station;
      const previousLoaded = sample.cannonLoaded;
      const hadSample = sample.tick > 0;
      if (!p || !r.buffer.sample(tick, sample)) continue;
      if (hadSample && sample.launchSerial !== previousSerial) r.cannonRecoil = this.reducedMotion ? 0 : 1;
      const ship = this.ships.get(frameSlot(sample.frame));
      const framePose = this.renderPoses[frameSlot(sample.frame)];
      const shipEpoch = ship?.pose.epoch ?? -1;
      const continuous = hadSample && sample.frame === r.prevFrame && sample.epoch === r.prevEpoch &&
        shipEpoch === r.prevShipEpoch && sample.mode === previousMode &&
        sample.launchSerial === previousSerial && sample.station === previousStation &&
        sample.cannonLoaded === previousLoaded;
      const dx = sample.x - r.prevX;
      const dz = sample.z - r.prevZ;
      r.prevX = sample.x;
      r.prevZ = sample.z;
      r.prevFrame = sample.frame;
      r.prevEpoch = sample.epoch;
      r.prevShipEpoch = shipEpoch;
      r.locomotion.distance = continuous && sample.mode !== MODE_LAUNCH ? Math.hypot(dx, dz) : 0;
      r.locomotion.discontinuous = !continuous;
      if (framePose) {
        // localToWorld's physical deck frame, carried by the hull's wave motion (not its sink/tilt).
        Matrix.RotationYToRef(framePose.heading, this.rideFlat);
        this.rideFlat.setTranslationFromFloats(framePose.x, 0, framePose.z).multiplyToRef(ship?.ride ?? Matrix.IdentityReadOnly, r.gaitFrame);
        r.locomotion.frameToWorld = r.gaitFrame;
      } else r.locomotion.frameToWorld = null;
      const remoteBar = capstanBar(sample.station);
      if (remoteBar >= 0 && ship && framePose) {
        // On the rendered head's own turn, so the hands stay on the bar between patches.
        capstanStation(remoteBar, ship.model.capstanTurn, this.capstanPose);
        localToWorld(framePose, this.capstanPose.x, this.capstanPose.y, this.capstanPose.z, tmp);
      } else if (framePose) localToWorld(framePose, sample.x, sample.y, sample.z, tmp);
      else { tmp.x = sample.x; tmp.y = sample.y; tmp.z = sample.z; }
      const climbPlayer = sample.mode !== MODE_LAUNCH && sample.station === p.station ? p : undefined;
      const climbShip = climbPlayer ? this.ships.get(climbPlayer.climbShip) : undefined;
      const ladder = climbTarget(climbPlayer, this.renderPoses[climbPlayer?.climbShip ?? -1] ?? null, climbShip?.generation ?? -1, this.remoteClimb);
      const position = this.ride(ladder && !ladder.tower ? ladder.key : frameSlot(sample.frame), r.climb.update(dt, climbPlayer, ladder, patchAge, tmp), this.remoteFeet);
      const distance = Math.hypot(position.x - this.camera.position.x, position.y - this.camera.position.y, position.z - this.camera.position.z);
      // Optical zoom (spyglass or mounted telescope) extends avatar range; revolver aim does not.
      const scopeMagnification = this.telescopeBlend > 0 ? this.magnification : 1 + (SPYGLASS.magnification - 1) * this.scopeBlend * this.scopeBlend * (3 - 2 * this.scopeBlend);
      const visible = !p.dead && p.crew >= 0 && distance <= Math.min(SPYGLASS.avatarRange, 250 * scopeMagnification);
      const contained = sample.cannonLoaded && this.isCannonStation(sample.station);
      r.avatar.setVisible(visible && !contained);
      r.avatar.setShadow(visible && !contained && distance <= 60);
      const pose = r.pose;
      // Step-held action fields come from the same delayed tick as the movement;
      // countdowns age from the sampled patch tick to the render tick.
      const age = Math.max(0, (tick - sample.actionTick) * DT);
      const attackLeft = sample.attackRemaining - age;
      pose.equipment = sample.equipment as Equipment;
      pose.bucketFull = sample.bucketFull;
      pose.station = sample.station;
      pose.attackPhase = attackLeft > 0 ? Math.min(sample.attackDuration, sample.attackDuration - attackLeft) : -1;
      pose.attackSerial = sample.attackSerial;
      pose.blocking = sample.blocking;
      pose.climbProgress = r.climb.progress;
      pose.wheel = ship?.model.wheelNode ?? null;
      pose.climbRoot = ladder?.tower ? this.env.fixtures.ladderRoots[p.climbLadder]! : climbShip?.model.root ?? null;
      pose.climbSpec = ladder?.spec;
      pose.cannon = this.stationBarrel(sample);
      pose.capstan = ship?.model.capstanNode ?? null;
      // Grips share the sampled aim; a local gunner keeps the authoritative barrel/camera.
      if (pose.cannon && pose.cannon !== localBarrel) {
        const aim = this.sampledBarrelAim;
        if (sample.station.startsWith("cannon:")) cannonAim(Number(sample.station.slice(7)), sample.yaw, sample.pitch, aim);
        else islandCannonAim(sample.station.slice(13) as IslandId, sample.yaw, sample.pitch, aim);
        pose.cannon.rotation.set(-aim.elevation, aim.yaw, 0);
      }
      const digElapsed = (tick - sample.digTick) * DT;
      const digging = sample.digTick >= 0 && digElapsed >= 0 && digElapsed < DIG_SWING_SECONDS &&
        sample.equipment === "shovel" && !sample.station && !sample.carrying && !sample.action && sample.recoverIn <= 0 &&
        sample.mode === MODE_WALK;
      pose.action = resolveCharacterAction({
        launching: sample.mode === MODE_LAUNCH, station: sample.station, carrying: sample.carrying !== "",
        swimming: sample.mode === MODE_SWIM, work: sample.action, recovering: sample.recoverIn > 0, digging,
      });
      pose.reducedMotion = this.reducedMotion;
      const scopeDuration = this.reducedMotion ? SPYGLASS.reducedTransitionSeconds : SPYGLASS.transitionSeconds;
      r.scopeBlend = Math.max(0, Math.min(1, r.scopeBlend + (sample.scopeRaised ? 1 : -1) * dt / scopeDuration));
      const stagger = Math.max(0, sample.staggerRemaining - age);
      r.recoil = Math.max(0, r.recoil - dt * (stagger > 0 ? 0.10 / MELEE.parryStagger : 0.6));
      r.parryRaise = Math.max(0, r.parryRaise - dt / 0.25);
      if (r.hitFlinch >= 0) r.hitFlinch = r.hitFlinch + dt < SWORD_FLINCH_SECONDS ? r.hitFlinch + dt : -1;
      r.cannonRecoil = Math.max(0, r.cannonRecoil - dt / 0.20);
      const pistolAimDuration = this.reducedMotion ? PISTOL.reducedAimTransitionSeconds : PISTOL.aimTransitionSeconds;
      r.pistolAimBlend = Math.max(0, Math.min(1, r.pistolAimBlend + (sample.pistolAiming ? 1 : -1) * dt / pistolAimDuration));
      r.pistolRecoil = Math.max(0, r.pistolRecoil - dt / PISTOL.recoilSeconds);
      pose.pistolRounds = sample.pistolRounds;
      pose.pistolReserve = sample.pistolReserve;
      pose.pistolAimBlend = r.pistolAimBlend;
      const reloadLeft = sample.pistolReloadRemaining - age;
      pose.pistolReloadProgress = reloadLeft > 0 ? Math.max(0, Math.min(1, 1 - reloadLeft / PISTOL.reloadSeconds)) : -1;
      pose.pistolRecoil = r.pistolRecoil;
      pose.attackStep = sample.attackStep;
      pose.attackDuration = sample.attackDuration;
      pose.staggerRemaining = stagger;
      pose.crouched = sample.crouched;
      pose.sprinting = sample.sprinting;
      const slideLeft = sample.slideRemaining - age;
      pose.slideProgress = slideLeft > 0 ? 1 - slideLeft / PLAYER.slideDuration : -1;
      pose.slideYaw = sample.slideYaw + (framePose?.heading ?? 0);
      pose.scopeBlend = r.scopeBlend;
      pose.pitch = sample.pitch;
      pose.detail = distance / this.magnification <= 30;
      pose.recoil = r.recoil;
      pose.parryRaise = r.parryRaise;
      pose.hitFlinch = r.hitFlinch;
      pose.hitStep = r.hitStep;
      pose.cannonRecoil = r.cannonRecoil;
      pose.actionProgress = digging ? digElapsed / DIG_SWING_SECONDS : sample.actionProgress;
      // Hold targets are not replicated: a repairer works the nearest damaged part.
      const repairShip = pose.action === "repair" ? this.room.state.crews.get(String(frameSlot(sample.frame)))?.ship : undefined;
      pose.repairStanding = !!repairShip && repairStanding(nearestRepair(repairShip, sample.x, sample.y, sample.z));
      pose.grounded = sample.grounded;
      pose.verticalSpeed = sample.vy;
      pose.waterHeave = sample.mode === MODE_SWIM ? this.swimHeave(position.x, position.y, position.z, dt) : 0;
      // Advance the timeline even while hidden, so reappearing never replays a transition.
      const state = r.animator.update(dt, pose);
      if (sample.attackSerial !== r.attackSerial) {
        r.attackSerial = sample.attackSerial;
        if (!p.dead && attackLeft > 0) {
          this.audioPos.x = position.x;
          this.audioPos.y = position.y + 1.1;
          this.audioPos.z = position.z;
          audio.play(sample.attackStep === 2 ? "swordFinisher" : "swordSwing", { position: this.audioPos });
        }
      }
      this.remoteAudio(r, p, ship?.generation ?? -1, position, dt);
      if (contained) {
        r.avatar.root.position.set(position.x, position.y, position.z);
        const barrel = this.sampledBarrelPosition(sample);
        if (barrel) this.placeTag(r, p, true, barrel);
        else r.tag.hidden = true;
        continue;
      }
      if (!visible) {
        r.avatar.root.position.set(position.x, position.y, position.z);
        r.tag.hidden = true;
        continue;
      }
      r.avatar.set(position.x, position.y, position.z, sample.yaw + (framePose?.heading ?? 0), r.locomotion, dt, pose, state);
      this.placeTag(r, p);
    }
  }

  private readonly tagWorld = new Vector3();
  private readonly tagScreen = new Vector3();
  private readonly viewport = new Viewport(0, 0, 1, 1);

  private placeTag(r: Remote, p: PlayerState, contained = false, barrel?: Readonly<Vec3>): void {
    const head = barrel ?? r.avatar.head.getAbsolutePosition();
    this.tagWorld.set(head.x, head.y + 0.40, head.z);
    const cam = this.camera.globalPosition;
    const dist = Vector3.Distance(cam, this.tagWorld);
    if (p.dead || p.crew < 0 || dist >= 80) {
      r.tag.hidden = true;
      return;
    }
    const w = this.engine.getRenderWidth();
    const hgt = this.engine.getRenderHeight();
    this.viewport.width = w;
    this.viewport.height = hgt;
    Vector3.ProjectToRef(this.tagWorld, Matrix.IdentityReadOnly, this.scene.getTransformMatrix(), this.viewport, this.tagScreen);
    const visible = !p.dead && dist < 80 && this.tagScreen.z > 0 && this.tagScreen.z < 1;
    r.tag.hidden = !visible;
    if (!visible) return;
    const crew = this.room.state.crews.get(String(p.crew));
    const displayName = p.name + (p.bot ? " (bot)" : "");
    const name = p.crew !== this.me()?.crew && crew ? `${displayName} · ${crew.name}` : displayName;
    const activity = contained ? `${name} · In cannon` : name;
    const label = p.connected ? activity : `${activity} · ⟳ Reconnecting…`;
    if (r.tag.textContent !== label) r.tag.textContent = label;
    const scale = this.engine.getHardwareScalingLevel();
    r.tag.style.transform = `translate(${(this.tagScreen.x * scale).toFixed(0)}px, ${(this.tagScreen.y * scale).toFixed(0)}px) translate(-50%, -100%)`;
  }

  private patchAge(): number {
    return Math.max(0, Math.min(1 / NET.patchRateHz, performance.now() / 1000 - this.patchAt));
  }

  private isCannonStation(station: string): boolean {
    return station.startsWith("cannon:") || station.startsWith("islandCannon:");
  }

  private stationBarrel(p: Pick<PlayerState, "station" | "frame">): TransformNode | null {
    let node: TransformNode | null = null;
    if (p.station.startsWith("cannon:")) {
      node = this.ships.get(frameSlot(p.frame))?.model.cannonNode(Number(p.station.slice(7))) ?? null;
    } else if (p.station.startsWith("islandCannon:")) {
      const id = p.station.slice(13);
      if (isIslandId(id)) node = this.islandCannons.get(id)?.barrelNode ?? null;
    }
    return node && !node.isDisposed() ? node : null;
  }

  private barrelPosition(node: TransformNode): Vector3 {
    Vector3.TransformCoordinatesToRef(this.barrelLocal, node.computeWorldMatrix(true), this.barrelWorld);
    return this.barrelWorld;
  }

  private readonly sampledBarrelWorld = new Vector3();
  private readonly sampledBarrelPivot: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly sampledBarrelAim = { yaw: 0, elevation: 0 };

  /** Delayed containment must not follow a barrel reset by a newer ownership patch. */
  private sampledBarrelPosition(sample: PoseSample): Readonly<Vec3> | undefined {
    if (!this.stationBarrel(sample)) return undefined;
    const pivot = this.sampledBarrelPivot;
    const aim = this.sampledBarrelAim;
    if (sample.station.startsWith("cannon:")) {
      const index = Number(sample.station.slice(7));
      const mount = SHIP_POINTS.cannons[index];
      const ship = this.renderPoses[frameSlot(sample.frame)];
      if (!mount || !ship) return undefined;
      localToWorld(ship, mount.x, SHIP.deckHeight + CANNON.pivotHeight, mount.z, pivot);
      cannonAim(index, sample.yaw, sample.pitch, aim);
      aim.yaw += ship.heading;
    } else {
      const id = sample.station.slice(13);
      if (!isIslandId(id)) return undefined;
      const mount = getIslandCannon(id);
      pivot.x = mount.point.x;
      pivot.y = mount.point.y;
      pivot.z = mount.point.z;
      islandCannonAim(id, sample.yaw, sample.pitch, aim);
    }
    const distance = CANNON.barrelLength + 0.05;
    const horizontal = Math.cos(aim.elevation) * distance;
    pivot.x += Math.sin(aim.yaw) * horizontal;
    pivot.y += Math.sin(aim.elevation) * distance;
    pivot.z += Math.cos(aim.yaw) * horizontal;
    return this.ride(frameSlot(sample.frame), pivot, this.sampledBarrelWorld);
  }

  private updateCamera(dt: number, me: PlayerState | undefined): void {
    const b = this.predictor.body;
    const prev = this.predictor.prev;
    const alpha = this.accumulator / DT;
    const c = this.predictor.correction;
    let x = b.x, y = b.y, z = b.z;
    if (prev.frame === b.frame) {
      x = prev.x + (b.x - prev.x) * alpha;
      y = prev.y + (b.y - prev.y) * alpha;
      z = prev.z + (b.z - prev.z) * alpha;
    }
    x += c.x; y += c.y; z += c.z;
    const capstanShip = this.ships.get(frameSlot(b.frame)), bar = capstanBar(me?.station ?? "");
    if (bar >= 0 && capstanShip) {
      const turn = capstanShip.model.capstanTurn;
      capstanStation(bar, turn, this.capstanPose);
      x = this.capstanPose.x; y = this.capstanPose.y; z = this.capstanPose.z;
      // Walking the bar round turns the view with it; on taking hold it settles along the bar.
      if (this.capstanViewTurn === null) this.capstanSettle = 0.4;
      else this.input.yaw = wrapAngle(this.input.yaw + turn - this.capstanViewTurn);
      this.capstanViewTurn = turn;
      if (this.capstanSettle > 0) {
        this.capstanSettle -= dt;
        this.input.yaw = wrapAngle(this.input.yaw + wrapAngle(this.capstanPose.yaw - this.input.yaw) * Math.min(1, dt * 10));
      }
    } else this.capstanViewTurn = null;
    const slot = frameSlot(b.frame);
    const framePose = this.renderPoses[slot];
    if (framePose) localToWorld(framePose, x, y, z, tmp);
    else { tmp.x = x; tmp.y = y; tmp.z = z; }
    const climbShip = this.ships.get(me?.climbShip ?? -1);
    const ladder = climbTarget(me, this.renderPoses[me?.climbShip ?? -1] ?? null, climbShip?.generation ?? -1, this.localClimb);
    const pos = this.ride(ladder && !ladder.tower ? ladder.key : slot, this.climb.update(dt, me, ladder, this.patchAge(), tmp), this.cameraFeet);
    this.selfFrame.feet.set(pos.x, pos.y, pos.z);
    this.selfFrame.localX = x; this.selfFrame.localZ = z; this.selfFrame.frame = b.frame;
    const yaw = this.input.yaw + (framePose?.heading ?? 0);
    const eye = me?.station ? PLAYER.eyeHeight : bodyEyeHeight(b);
    // Keep both world-space rung grips in view instead of putting the eye against the hull.
    const onLadder = !!ladder;
    if (ladder) this.ladderViewHeading = ladder.tower ? ladder.tower.frame.heading : climbShip!.pose.yaw;
    if (!me || me.dead || !me.connected) this.ladderViewOffset = 0;
    else this.ladderViewOffset += ((onLadder ? 0.45 : 0) - this.ladderViewOffset) * Math.min(1, dt / 0.12);
    this.feetWorld(this.logicalEye);
    this.logicalEye.y += eye;
    const pitchCos = Math.cos(this.input.pitch);
    this.logicalForward.set(Math.sin(yaw) * pitchCos, -Math.sin(this.input.pitch), Math.cos(yaw) * pitchCos);

    this.attackPrediction = Math.max(0, this.attackPrediction - dt);
    if (me?.attackSerial === this.predictedSerial && me.attackRemaining > 0) this.attackPrediction = 0;
    if (me && me.attackSerial !== this.observedAttackSerial) {
      this.observedAttackSerial = me.attackSerial;
      if (this.strikeHitSerial !== me.attackSerial) this.strikeHitSerial = -1;
      if (this.queuedSerial === me.attackSerial) this.queuedSerial = 0;
      if (me.attackRemaining > 0 && me.attackSerial !== this.playedAttackSerial) {
        this.playedAttackSerial = me.attackSerial;
        audio.play(me.attackStep === 2 ? "swordFinisher" : "swordSwing", { position: this.swingPosition() });
      }
    }
    if (!me || !this.canMelee(me)) {
      this.attackPrediction = 0;
      this.pendingQueueRequest = this.queuedSerial = 0;
      this.strikeHitSerial = -1;
    }
    // The presented swing runs on its own clock from the click (or the authoritative start), so its
    // phase never steps backwards with patch timing; it eases toward authority instead.
    if (this.attackClock >= 0) this.attackClock += dt;
    // An acknowledged queued cut starts as soon as the current one ends locally, on its own
    // step's timing, rather than holding until the authoritative patch lands a round trip later.
    if (me && this.queuedSerial !== 0 && me.attackSerial !== this.queuedSerial && me.attackStep < 2 &&
      (this.attackClock < 0 || this.attackClock >= this.displayAttackDuration)) {
      const step = me.attackStep + 1, timing = MELEE.combo[step]!;
      this.attackClock = this.attackClock >= 0 ? Math.min(0.1, this.attackClock - this.displayAttackDuration) : 0;
      this.attackClockSerial = this.predictedSerial = this.playedAttackSerial = this.queuedSerial;
      this.queuedSerial = 0;
      this.displayAttackStep = step;
      this.displayAttackDuration = timing.windup + timing.active + timing.recovery;
      this.attackPrediction = this.displayAttackDuration - this.attackClock;
      audio.play(step === 2 ? "swordFinisher" : "swordSwing", { position: this.swingPosition() });
    }
    if (me && me.attackRemaining > 0) {
      const elapsed = me.attackDuration - Math.max(0, me.attackRemaining - this.patchAge());
      if (me.attackSerial === this.attackClockSerial) {
        if (this.attackClock >= 0) {
          // Misses and blocks extend the authoritative recovery.
          this.displayAttackStep = me.attackStep;
          this.displayAttackDuration = me.attackDuration;
          const drift = elapsed - this.attackClock;
          if (Math.abs(drift) > ATTACK_CLOCK_SNAP) this.attackClock = elapsed;
          else this.attackClock += dt * Math.max(-0.5, Math.min(0.5, drift * 4));
        }
      } else if (this.attackClock < 0 || ((me.attackSerial - this.attackClockSerial) >>> 0) < 0x80000000) {
        // A swing this client did not predict, or one newer than it: adopt the authoritative timing.
        this.attackClockSerial = me.attackSerial;
        this.attackClock = elapsed;
        this.displayAttackStep = me.attackStep;
        this.displayAttackDuration = me.attackDuration;
      }
    }
    // Parried strikes end at once; a finished swing holds its last pose only while the next cut is pending.
    if (!me || !this.canMelee(me) || (me.attackSerial === this.attackClockSerial && me.attackDuration <= 0) ||
      (this.attackClock >= this.displayAttackDuration && !this.queuedSerial && !this.pendingQueueRequest)) this.attackClock = -1;
    const attackPhase = this.attackPhase = this.attackClock >= 0 ? Math.min(this.attackClock, this.displayAttackDuration) : -1;
    if (this.hitFlinch >= 0) this.hitFlinch = this.hitFlinch + dt < SWORD_FLINCH_SECONDS ? this.hitFlinch + dt : -1;
    this.recoil = Math.max(0, this.recoil - dt * (me?.staggerRemaining ? 0.10 / MELEE.parryStagger : 0.6));
    this.parryRaise = Math.max(0, this.parryRaise - dt / 0.25);
    const sailing = this.room.state.crews.get(String(slot))?.ship;
    const sailTarget = sailing && !sailing.anchored && !sailing.sinking ? Math.min(1, Math.abs(sailing.speed) / 8) : 0;
    this.sailBlend += (sailTarget - this.sailBlend) * Math.min(1, dt / 0.25);
    if (!me || me.dead || !me.connected || !this.input.active) this.resetScope();
    const oldScope = this.scopeBlend;
    const raiseScope = !!me && this.scopeIntent(me);
    const scopeDuration = this.reducedMotion ? SPYGLASS.reducedTransitionSeconds : SPYGLASS.transitionSeconds;
    this.scopeBlend = Math.max(0, Math.min(1, this.scopeBlend + (raiseScope ? 1 : -1) * dt / scopeDuration));
    if ((this.scopeBlend === 1 && oldScope < 1) || (this.scopeBlend === 0 && oldScope > 0)) audio.play("spyglass");
    const raisePistol = !!me && this.pistolAimIntent(me);
    const pistolAimDuration = this.reducedMotion ? PISTOL.reducedAimTransitionSeconds : PISTOL.aimTransitionSeconds;
    this.pistolAimBlend = Math.max(0, Math.min(1, this.pistolAimBlend + (raisePistol ? 1 : -1) * dt / pistolAimDuration));
    const oldTelescope = this.telescopeBlend;
    const atTelescope = !!me && !me.dead && isScopeStation(me.station);
    this.telescopeBlend = Math.max(0, Math.min(1, this.telescopeBlend + (atTelescope ? 1 : -1) * dt / (scopeDuration * 2)));
    if ((this.telescopeBlend === 1 && oldTelescope < 1) || (this.telescopeBlend === 0 && oldTelescope > 0)) audio.play("spyglass");
    this.magnification = lensMagnification(this.scopeBlend, this.pistolAimBlend, this.telescopeBlend);
    this.input.setLookScale(1 / this.magnification);
    this.hud.setScope(Math.max(this.scopeBlend, this.telescopeBlend), this.telescopeBlend > 0 ? FIXTURES.telescopeMagnification : SPYGLASS.magnification);
    const view = this.viewFrame;
    view.dt = dt; view.time = this.lastFrame / 1000; view.feet = pos;
    this.selfHeave = b.mode === MODE_SWIM ? this.swimHeave(pos.x, pos.y, pos.z, dt) : 0;
    view.waterHeave = this.selfHeave;
    view.targetEye = b.slideRemaining > 0 ? PLAYER.slideEyeHeight : eye;
    view.offsetX = Math.cos(this.ladderViewHeading) * this.ladderViewOffset;
    view.offsetZ = -Math.sin(this.ladderViewHeading) * this.ladderViewOffset;
    view.yaw = yaw; view.pitch = this.input.pitch;
    view.speed = bodySpeed(prev, b);
    view.walking = !!me && !me.dead && !me.station && !this.flightActive && b.grounded && b.mode === MODE_WALK;
    view.grounded = b.grounded; view.onStation = !!me?.station; view.atWheel = me?.station === "wheel";
    view.ladder = me?.station === "ladder"; view.climbProgress = this.climb.progress;
    view.crouched = b.crouched; view.swimming = b.mode === MODE_SWIM; view.bodyHeight = bodyHeight(b); view.sprinting = b.sprinting; view.carrying = !!me?.carrying;
    view.slideProgress = b.slideRemaining > 0 ? 1 - b.slideRemaining / PLAYER.slideDuration : -1;
    view.scopeBlend = this.scopeBlend; view.telescopeBlend = this.telescopeBlend; view.magnification = this.magnification;
    view.sailBlend = this.sailBlend; view.rudder = sailing?.rudder ?? 0;
    view.reducedMotion = this.reducedMotion;
    view.decorate = !!me && !me.dead && !me.cannonLoaded;
    view.attackPhase = attackPhase; view.attackStep = this.displayAttackStep;
    view.hitFlinch = this.hitFlinch; view.hitStep = this.hitStep;
    this.firstPerson.update(this.camera, view);
    this.eyeHeight = this.firstPerson.eyeHeight;
    this.thirdPersonActive = this.view === "third" && !!me && !me.dead && me.connected && me.crew >= 0 &&
      this.scopeBlend === 0 && this.telescopeBlend === 0;
    if (this.thirdPersonActive) {
      // Presentation only: logicalEye/logicalForward stay at the eye for aim and interaction.
      // Follow the rendered (interpolated) feet, not the 30 Hz physics body, so the sailor never judders in frame.
      const origin = this.chaseOrigin, back = this.chaseBack;
      origin.x = pos.x; origin.y = pos.y + this.eyeHeight + THIRD_PERSON.height; origin.z = pos.z;
      back.x = -this.logicalForward.x; back.y = -this.logicalForward.y; back.z = -this.logicalForward.z;
      const d = thirdPersonDistance(origin, back, this.renderPoses);
      this.camera.position.set(origin.x + back.x * d, origin.y + back.y * d, origin.z + back.z * d);
      this.camera.rotation.set(this.input.pitch, yaw, 0);
    }
    const barrel = me?.cannonLoaded ? this.stationBarrel(me) : null;
    if (barrel) {
      this.camera.position.copyFrom(this.barrelPosition(barrel));
      Vector3.TransformNormalToRef(this.barrelAxis, barrel.computeWorldMatrix(true), this.barrelForward);
      this.barrelForward.normalize();
      this.camera.rotation.set(-Math.asin(this.barrelForward.y), Math.atan2(this.barrelForward.x, this.barrelForward.z), 0);
      this.logicalForward.copyFrom(this.barrelForward);
      this.logicalEye.copyFrom(this.camera.position);
    }
    if (this.spectating) this.spectateCamera();
  }

  // ---------------------------------------------------------------------------
  // Battle royale

  /** Crewless sailors in the battle royale watch the surviving ships. */
  private get spectating(): boolean {
    return this.royale && (this.me()?.crew ?? -1) < 0;
  }

  private spectateAlive(slot: number): boolean {
    if (!this.ships.has(slot) || !this.renderPoses[slot]) return false;
    const ship = this.room.state.crews.get(String(slot))?.ship;
    return !!ship && !ship.sinking;
  }

  private spectateCamera(): void {
    if (!this.spectateAlive(this.spectateSlot)) {
      this.spectateSlot = -1;
      for (const slot of [...this.ships.keys()].sort((a, b) => a - b)) {
        if (this.spectateAlive(slot)) {
          this.spectateSlot = slot;
          break;
        }
      }
      if (this.spectateSlot < 0) return;
    }
    const p = this.renderPoses[this.spectateSlot]!;
    const yaw = this.input.yaw;
    const pitch = Math.min(1.1, Math.max(0.12, 0.35 + this.input.pitch * 0.5));
    const d = 38;
    if (this.camera.parent) this.camera.parent = null;
    this.camera.position.set(p.x - Math.sin(yaw) * Math.cos(pitch) * d, 3 + Math.sin(pitch) * d, p.z - Math.cos(yaw) * Math.cos(pitch) * d);
    this.camera.rotation.set(pitch, yaw, 0);
  }

  private cycleSpectate(): void {
    const alive = [...this.ships.keys()].filter((slot) => this.spectateAlive(slot)).sort((a, b) => a - b);
    if (alive.length === 0) return;
    this.spectateSlot = alive.find((slot) => slot > this.spectateSlot) ?? alive[0]!;
  }

  private updateRoyaleWorld(dt: number): void {
    const s = this.room.state;
    if (this.ringMesh) this.ringMesh.scaling.x = this.ringMesh.scaling.z = s.royale.radius;
    for (const [id, m] of this.loot) {
      if (!s.loot.has(id)) {
        m.dispose();
        this.loot.delete(id);
      }
    }
    s.loot.forEach((l, id) => {
      let m = this.loot.get(id);
      if (!m) {
        m = new LootModel(this.scene, this.env);
        this.loot.set(id, m);
      }
      m.root.position.set(l.x, m.float(dt, this.reducedMotion), l.z);
    });
    const chest = s.royale.treasureId ? this.chests.get(s.royale.treasureId) : undefined;
    if (this.treasureBeacon) {
      this.treasureBeacon.setEnabled(!!chest);
      if (chest) {
        const at = chest.root.getAbsolutePosition();
        this.treasureBeacon.position.set(at.x, at.y + 30, at.z);
      }
    }
    this.updateTreasurePointer(chest?.root.getAbsolutePosition());
  }

  /** Battle royale: an on-screen marker on the treasure, pinned to the screen edge (with an arrow) when it is off-screen. */
  private updateTreasurePointer(at: Vector3 | undefined): void {
    const r = this.room.state.royale, own = this.me()?.crew ?? -1;
    if (!at || r.phase !== "active" || (r.holder >= 0 && r.holder === own)) return this.hud.setTreasurePointer(null);
    const cam = this.camera.globalPosition;
    const distance = Math.round(Math.hypot(at.x - cam.x, at.z - cam.z));
    const holder = r.holder >= 0 ? this.room.state.crews.get(String(r.holder))?.name : undefined;
    const label = `${holder ? `★ ${holder}` : "★ Treasure"} · ${distance} m`;
    const w = this.engine.getRenderWidth(), hgt = this.engine.getRenderHeight(), scale = this.engine.getHardwareScalingLevel();
    this.viewport.width = w;
    this.viewport.height = hgt;
    this.tagWorld.set(at.x, at.y + 2, at.z);
    Vector3.ProjectToRef(this.tagWorld, Matrix.IdentityReadOnly, this.scene.getTransformMatrix(), this.viewport, this.tagScreen);
    const margin = 70 / scale;
    const onScreen = this.tagScreen.z > 0 && this.tagScreen.z < 1 &&
      this.tagScreen.x > margin && this.tagScreen.x < w - margin && this.tagScreen.y > margin && this.tagScreen.y < hgt - margin;
    if (onScreen) return this.hud.setTreasurePointer({ x: this.tagScreen.x * scale, y: this.tagScreen.y * scale, arrow: null, label });
    // Off-screen: bearing relative to the view, placed on an ellipse inside the screen edge (0 = straight ahead = top).
    const bearing = wrapAngle(Math.atan2(at.x - cam.x, at.z - cam.z) - this.camera.absoluteRotation.toEulerAngles().y);
    const cx = (w * scale) / 2, cy = (hgt * scale) / 2, rx = cx - 70, ry = cy - 70;
    this.hud.setTreasurePointer({ x: cx + Math.sin(bearing) * rx, y: cy - Math.cos(bearing) * ry, arrow: bearing, label });
  }

  private updateRoyaleHud(me: PlayerState | undefined, own: CrewState | undefined): void {
    const s = this.room.state;
    const r = s.royale;
    const snap: RoyaleSnapshot = {
      phase: r.phase as RoyalePhase, phaseIn: r.phaseIn, round: r.round, alive: r.alive, radius: r.radius,
      winner: r.winner, winnerSlot: r.winnerSlot, winReason: r.winReason, crew: me?.crew ?? -1, queued: me?.queued ?? false,
    };
    this.hud.setPhaseChip(phaseChip(snap));
    // `enabled` is false until the first state patch; a default "waiting" frame would fake a transition.
    if (r.enabled) {
      const splash = royaleSplash(this.royaleSnap, snap);
      if (splash) this.hud.showPhaseSplash(splash);
      this.royaleSnap = snap;
    }
    this.hud.setRoyaleResults(royaleResults(snap));
    const treasure = r.treasureId ? this.chests.get(r.treasureId)?.root.getAbsolutePosition() : undefined;
    let objective: string;
    if (r.phase === "waiting" || r.phase === "countdown") objective = "Man your ship. Loot barrels restock planks and cannonballs.";
    else if (r.phase === "ended") objective = "Round over. Get ready for the next one.";
    else if (!r.treasureId) objective = `Treasure surfaces in ${Math.ceil(r.treasureIn)}s.`;
    else if (r.holder >= 0 && r.holder === own?.slot) objective = `Hold the treasure: ${Math.floor(r.holdTime)}/${BATTLE_ROYALE.treasureHold}s. Survive!`;
    else if (r.holder >= 0) objective = `The ${s.crews.get(String(r.holder))?.name ?? "rivals"} hold the treasure (${Math.floor(r.holdTime)}/${BATTLE_ROYALE.treasureHold}s). Sink them!`;
    else {
      const from = own ? this.feetWorld(tmp) : this.renderPoses[this.spectateSlot] ?? this.feetWorld(tmp);
      objective = treasure ? `Treasure adrift ${Math.round(Math.hypot(treasure.x - from.x, treasure.z - from.z))} m away. See map (M).` : "Treasure adrift. See map (M).";
    }
    this.hud.setQuestMap(null, 0);
    this.hud.setObjective(objective, "E: interact · M: map");
    this.hud.setBanner("vote", null);
    this.hud.setBanner("boundary", null);
    this.hud.setShipVisible(!!own);

    if (!own) {
      this.hud.setBanner("royaleRing", null);
      this.hud.setSinking(null, () => "");
      const target = r.phase !== "waiting" && this.spectateAlive(this.spectateSlot) ? s.crews.get(String(this.spectateSlot)) : undefined;
      this.hud.setSpectator(spectatorView(snap, target?.name ?? null));
    } else {
      this.hud.setSpectator(null);
      const ship = own.ship;
      const stationShip = s.crews.get(String(frameSlot(me?.frame ?? 0)))?.ship;
      const helm = me?.station === "wheel" && stationShip ? { speed: Math.abs(stationShip.speed), heading: stationShip.heading, sail: stationShip.sail } : null;
      this.hud.setShip(ship, helm, ship.anchored, ship.docked, "the start line");
      const reload = me?.station.startsWith("cannon:") ? (stationShip?.cannons.at(Number(me.station.slice(7)))?.reload ?? 0) : null;
      this.hud.setSupplies(ship.cannonballs, ship.planks, reload, me?.cannonLoaded ?? false);
      this.hud.setSinking(ship.sinking ? ship.sinkIn : null, (n) => r.phase === "active"
        ? `The ship goes down in ${n}… your crew is out of this round.`
        : `A fresh ship awaits at the start line in ${n}…`);
      if (ship.sinking) this.hud.closeSale();
      if (this.hud.paused) this.hud.setMenuState(!!me && !me.dead && !this.flightActive && me.recoverIn <= 0, false);
      this.hud.setBanner("recover", me && me.recoverIn > 0 ? `Recovering in ${Math.ceil(me.recoverIn)}… stay still.` : null);
      const pose = this.renderPoses[own.slot];
      this.hud.setBanner("royaleRing", pose && royaleDistance(pose.x, pose.z) > r.radius ? "Outside the ring! Your hull is flooding. Steer back inside." : null);
      if (this.predictor.body.frame > 0) this.completeStep("board");
      if (me?.station === "wheel") this.completeStep("wheel");
    }

    if (this.hud.mapOpen) {
      this.hud.setMapRing(r.radius);
      const markers: MapMarker[] = [];
      for (const slot of this.ships.keys()) {
        const pose = this.renderPoses[slot];
        const crew = s.crews.get(String(slot));
        if (!pose || !crew || crew.ship.sinking) continue;
        markers.push({ x: pose.x, z: pose.z, heading: pose.heading, label: slot === own?.slot ? "Your ship" : crew.name, kind: "ship" });
      }
      if (own) {
        const feet = this.feetWorld(tmp);
        markers.push({ x: feet.x, z: feet.z, heading: this.camera.absoluteRotation.toEulerAngles().y, label: "You", kind: "self" });
        for (const [id, rem] of this.remotes) {
          const p = s.players.get(id);
          if (!p || p.dead || p.crew !== own.slot) continue;
          const wp = rem.avatar.root.getAbsolutePosition();
          markers.push({ x: wp.x, z: wp.z, heading: 0, label: p.name, kind: "mate" });
        }
      }
      if (treasure) markers.push({ x: treasure.x, z: treasure.z, heading: 0, label: "Treasure", kind: "treasure" });
      s.loot.forEach((l) => markers.push({ x: l.x, z: l.z, heading: 0, label: "", kind: "loot" }));
      this.hud.setMapMarkers(markers);
    }
  }

  /** Rendered sea height under a swimmer at physical feet height `y`, fading out over the first half metre of a dive. */
  private swimHeave(x: number, y: number, z: number, dt: number): number {
    const surface = 1 - Math.min(1, Math.max(0, (PLAYER.surfaceFeetY - y) / 0.5));
    return surface > 0 ? surface * this.env.seaHeight(x, z, dt, this.reducedMotion) : 0;
  }

  private updateSelfPresentation(dt: number, me: PlayerState | undefined): void {
    const b = this.predictor.body, prev = this.predictor.prev;
    const speed = this.flightActive ? 0 : bodySpeed(prev, b);
    const ship = this.ships.get(frameSlot(b.frame));
    const climbShip = this.ships.get(me?.climbShip ?? -1);
    const tower = me?.station === "ladder" ? WORLD_LADDERS[me.climbLadder] : undefined;
    this.shovelSwing = Math.max(0, this.shovelSwing - dt / DIG_SWING_SECONDS);
    const pose = this.selfPose;
    const bodyVisible = !!me && !me.dead && me.connected && me.crew >= 0 && !me.cannonLoaded && this.telescopeBlend < 0.5;
    pose.visible = bodyVisible;
    pose.equipment = (me?.equipment ?? "shovel") as Equipment;
    pose.bucketFull = me?.bucketFull ?? false;
    pose.station = me?.station ?? "";
    pose.detail = true;
    pose.action = resolveCharacterAction({
      launching: this.flightActive, station: pose.station, carrying: !!me?.carrying, swimming: b.mode === MODE_SWIM,
      work: me?.action ?? "", recovering: !!me && me.recoverIn > 0, digging: this.shovelSwing > 0,
    });
    pose.attackPhase = this.attackPhase;
    pose.attackSerial = this.attackPrediction > 0 ? this.predictedSerial : me?.attackSerial ?? 0;
    pose.blocking = !!me?.blocking;
    pose.actionProgress = this.shovelSwing > 0 ? 1 - this.shovelSwing : me?.actionProgress ?? 0;
    pose.repairStanding = pose.action === "repair" && repairStanding(this.holding);
    pose.climbProgress = this.climb.progress;
    pose.reducedMotion = this.reducedMotion;
    pose.wheel = ship?.model.wheelNode ?? null;
    pose.climbRoot = tower ? this.env.fixtures.ladderRoots[me!.climbLadder]! : climbShip?.model.root ?? null;
    pose.climbSpec = tower?.spec ?? (me && me.station === "ladder" && me.climbShip >= 0 ? SHIP_LADDERS[me.shipLadder] : undefined);
    pose.cannon = me ? this.stationBarrel(me) : null;
    pose.capstan = ship?.model.capstanNode ?? null;
    pose.attackStep = this.displayAttackStep;
    pose.attackDuration = this.displayAttackDuration;
    pose.staggerRemaining = me?.staggerRemaining ?? 0;
    applyBodyToPose(pose, b, speed, this.renderPoses[frameSlot(b.frame)]?.heading ?? 0);
    pose.waterHeave = this.selfHeave;
    pose.scopeBlend = this.scopeBlend;
    pose.pitch = this.input.pitch;
    pose.recoil = this.recoil;
    pose.parryRaise = this.parryRaise;
    pose.hitFlinch = this.hitFlinch;
    pose.hitStep = this.hitStep;
    this.cannonRecoil = Math.max(0, this.cannonRecoil - dt / 0.20);
    pose.cannonRecoil = this.cannonRecoil;
    this.pistolRecoil = Math.max(0, this.pistolRecoil - dt / PISTOL.recoilSeconds);
    pose.pistolRounds = me?.pistolRounds ?? PISTOL.capacity;
    pose.pistolReserve = me?.pistolReserve ?? 0;
    pose.pistolAimBlend = this.pistolAimBlend;
    pose.pistolReloadProgress = me && me.pistolReloadRemaining > 0 ?
      Math.max(0, Math.min(1, 1 - (me.pistolReloadRemaining - this.patchAge()) / PISTOL.reloadSeconds)) : -1;
    pose.pistolRecoil = this.pistolRecoil;
    // The timeline runs while hidden too, so a toggle or reappearance never restarts an action.
    const state = this.selfAnimator.update(dt, pose);
    const avatar = this.selfAvatar;
    if (!bodyVisible) { avatar.setVisible(false); this.selfPrevFrame = -1; return; }
    avatar.setVisible(true);
    avatar.setShadow(true);
    avatar.setFirstPerson(this.thirdPersonActive ? null : this.camera);
    const f = this.selfFrame, loco = this.selfLocomotion;
    const distance = Math.hypot(f.localX - this.selfPrevX, f.localZ - this.selfPrevZ);
    loco.discontinuous = this.selfPrevFrame < 0 || f.frame !== this.selfPrevFrame || distance > 6;
    loco.distance = loco.discontinuous || this.flightActive ? 0 : distance;
    this.selfPrevX = f.localX; this.selfPrevZ = f.localZ; this.selfPrevFrame = f.frame;
    const slot = frameSlot(f.frame), framePose = this.renderPoses[slot];
    if (framePose) {
      // localToWorld's physical deck frame, carried by the hull's wave motion (not its sink/tilt),
      // exactly as remote gait frames are, so planted feet bob with the deck.
      Matrix.RotationYToRef(framePose.heading, this.rideFlat);
      this.rideFlat.setTranslationFromFloats(framePose.x, 0, framePose.z).multiplyToRef(this.ships.get(slot)?.ride ?? Matrix.IdentityReadOnly, this.selfGaitFrame);
      loco.frameToWorld = this.selfGaitFrame;
    } else loco.frameToWorld = null;
    avatar.set(f.feet.x, f.feet.y, f.feet.z, this.input.yaw + (framePose?.heading ?? 0), loco, dt, pose, state);
  }

  /** Rebuilds a sailor's avatar after their look changes, keeping its place in the scene. */
  private relook(old: Avatar, id: string, look: string): Avatar {
    // A chest riding the old rig is re-parented to the new carry point by updateChests.
    for (const chest of this.chests.values()) if (chest.root.parent === old.carryPoint) chest.root.setParent(null);
    const avatar = new Avatar(this.scene, this.env, id, decodeLook(look));
    avatar.root.parent = old.root.parent;
    avatar.setVisible(false);
    old.dispose();
    return avatar;
  }

  private updateChests(dt: number): void {
    const chests = this.room.state.chests;
    for (const [id, m] of this.chests) {
      if (!chests.has(id)) {
        m.dispose();
        this.chests.delete(id);
      }
    }
    chests.forEach((c, id) => {
      let m = this.chests.get(id);
      if (!m) {
        m = new ChestModel(this.scene, this.env, c.tier);
        this.chests.set(id, m);
      }
      const root = m.root;
      if (c.loc === CHEST_CARRIED) {
        // Every carrier, the local sailor included, holds the chest at its avatar's carry point.
        const parent = c.carrier === this.room.sessionId ? this.selfAvatar.carryPoint : this.remotes.get(c.carrier)?.avatar.carryPoint ?? null;
        if (root.parent !== parent) root.parent = parent;
        root.position.set(0, -0.25, 0);
        root.rotation.set(0, 0, 0);
      } else if (c.loc === CHEST_SHIP) {
        const parent = this.ships.get(c.ship)?.model.root ?? null;
        if (root.parent !== parent) root.parent = parent;
        root.position.set(c.x, c.y, c.z);
        root.rotation.set(0, c.yaw, 0);
      } else {
        if (root.parent !== null) root.parent = null;
        const y = c.loc === CHEST_FLOATING ? m.float(dt, this.reducedMotion) : c.y;
        root.position.set(c.x, y, c.z);
        root.rotation.set(0, c.yaw, 0);
      }
    });
    // Dig mounds from shared state.
    const digs = this.room.state.digs;
    while (this.mounds.length < digs.length) this.mounds.push(digMound(this.scene, this.env));
    for (let i = 0; i < this.mounds.length; i++) {
      const d = digs.at(i);
      const mound = this.mounds[i]!;
      mound.setEnabled(!!d);
      if (d) mound.position.set(d.x, d.y + 0.05, d.z);
    }
  }

  // ---------------------------------------------------------------------------
  // Interaction targeting (client-side prompt; the server re-validates).

  private updateTarget(me: PlayerState | undefined): void {
    this.target = null;
    if (me && isScopeStation(me.station) && !me.dead) {
      this.hud.setPrompt("Mouse  Sweep the horizon · E  Step away from the telescope", null);
      return;
    }
    if (!me || me.dead || this.flightActive || !this.input.active || this.scopeBlend > 0 || me.scopeRaised) {
      this.hud.setPrompt(null, null);
      return;
    }
    if (me.station === "ladder") {
      this.hud.setPrompt("W/S  Climb · Space  Jump off", me.climbProgress);
      return;
    }
    if (me.station === "wheel") {
      this.hud.setPrompt("E  Leave the wheel", null);
      return;
    }
    if (this.isCannonStation(me.station)) {
      const label = me.cannonLoaded ? "Left click  Launch yourself · E  Climb out" :
        me.station.startsWith("islandCannon:") ? "Mouse  Aim · F  Climb in · E  Leave the cannon" :
        "Left click  Fire cannonball · F  Climb in · E  Leave the cannon";
      this.hud.setPrompt(label + (this.landingRing.isEnabled() && (me.cannonLoaded || me.station.startsWith("islandCannon:")) ? " · Approximate landing" : ""), null);
      return;
    }
    if (capstanBar(me.station) >= 0) {
      const ship = this.room.state.crews.get(String(frameSlot(this.predictor.body.frame)))?.ship;
      let pushers = 0;
      this.room.state.players.forEach((p) => { if (capstanBar(p.station) >= 0 && p.frame === me.frame) pushers++; });
      this.hud.setPrompt(`Raising the anchor… keep holding E${pushers > 1 ? ` · ${pushers} on the bars` : ""}`, ship ? 1 - ship.anchorDepth : null);
      return;
    }
    if (me.action === "repair" || me.action === "fill" || me.action === "throw") {
      const label = me.action === "repair" ? repairingPrompt(this.room.state.crews.get(String(frameSlot(this.predictor.body.frame)))?.ship, this.holding)
        : me.action === "fill" ? "Filling the bucket…" : "Throwing the water overboard";
      this.hud.setPrompt(label, me.action === "throw" ? null : me.actionProgress);
      return;
    }
    const body = this.predictor.body;
    const bucketAction = this.bucketAction(me);
    const bucketHint = bucketAction === "fill" ? "Left click  Fill the bucket from the flood water"
      : bucketAction === "throw" ? "Left click  Throw the water overboard"
      : bucketAction === "carry" ? "Carry the water to the rail and face the sea" : null;
    const s = this.room.state;

    const eye = this.logicalEye;
    const fwd = this.logicalForward;
    const cands: Target[] = [];
    for (const [slot] of this.ships) {
      const pose = this.renderPoses[slot]!;
      const ship = s.crews.get(String(slot))?.ship;
      if (!ship || ship.sinking || (frameSlot(body.frame) !== slot && Math.hypot(eye.x - pose.x, eye.z - pose.z) >= 20)) continue;
      const shipPoint = (id: UseTarget | HoldTarget, p: Vec3, label: string, hold: boolean, tap?: UseTarget): void => {
        localToWorld(pose, p.x, p.y, p.z, tmp);
        cands.push({ id, hold, label, x: tmp.x, y: tmp.y, z: tmp.z, tap });
      };
      const owner = ship.wheelOwner ? s.players.get(ship.wheelOwner) : undefined;
      const helm = repairStep(ship, "wheel", repairScratch);
      if (helm && wheelBroken(ship)) shipPoint("wheel", SHIP_POINTS.wheel, repairPrompt(ship, "wheel", helm), true);
      else if (owner) shipPoint("wheel", SHIP_POINTS.wheel, `Wheel in use by ${owner.name}`, false);
      // A hacked but working helm: tap E to steer, hold E to mend it.
      else if (helm) shipPoint("wheel", SHIP_POINTS.wheel, `E  Take the wheel · ${repairPrompt(ship, "wheel", helm)}`, true, "wheel");
      else shipPoint("wheel", SHIP_POINTS.wheel, "E  Take the wheel", false);
      shipPoint("capstan", SHIP_POINTS.capstan, !ship.anchored ? "E  Drop the anchor" : ship.anchorFalling ? "The anchor is running out…" : "Hold E  Raise the anchor", ship.anchored);
      shipPoint("bucket", SHIP_POINTS.bucketRack, me.equipment === "bucket" ? "E  Put the bucket back" : "E  Take a bucket", false);
      for (const target of REPAIR_TARGETS) {
        if (target === "wheel") continue;
        const step = repairStep(ship, target, repairScratch);
        if (step) shipPoint(target, repairPromptPoint(target, step, tmp2), repairPrompt(ship, target, step), true);
      }
      for (let i = 0; i < SHIP_POINTS.cannons.length; i++) {
        const cannon = ship.cannons.at(i);
        if (cannon?.wrecked) continue;
        const gunner = cannon?.owner ? s.players.get(cannon.owner) : undefined;
        shipPoint(`cannon:${i}`, SHIP_POINTS.cannons[i]!.point, gunner ? `Cannon in use by ${gunner.name}` : "E  Man the cannon", false);
      }
      if (ship.docked) shipPoint("supplies", SHIP_POINTS.supplies, "E  Restock supplies", false);
      shipPoint("bell", SHIP_POINTS.bell, "E  Ring the ship's bell", false);
      SHIP_POINTS.lanterns.forEach((point, i) => {
        shipPoint(`shipLantern:${i}`, point, ship.lanterns & (1 << i) ? "E  Douse the lantern" : "E  Light the lantern", false);
      });
      let lookout: string | undefined;
      s.players.forEach((p) => { if (p.station === "nestScope" && frameSlot(p.frame) === slot) lookout = p.name; });
      shipPoint("nestScope", SHIP_POINTS.nestScope, lookout ? `Telescope in use by ${lookout}` : "E  Look through the telescope", false);
      if (frameSlot(body.frame) === slot) {
        localToWorld(pose, SHIP_POINTS.mapTable.x, SHIP_POINTS.mapTable.y, SHIP_POINTS.mapTable.z, tmp);
        cands.push({ id: "mapTable", hold: false, label: "E  Study the chart", x: tmp.x, y: tmp.y, z: tmp.z });
      }
    }
    for (const mount of ISLAND_CANNONS) {
      const cannon = s.islandCannons.get(mount.id);
      const gunner = cannon?.owner ? s.players.get(cannon.owner) : undefined;
      cands.push({ id: `islandCannon:${mount.id}`, hold: false,
        label: gunner ? `Cannon in use by ${gunner.name}` : "E  Use shore cannon",
        x: mount.point.x, y: mount.point.y, z: mount.point.z, visible: true });
    }
    const crew = this.ownCrew();
    const lantern = s.lantern;
    const freeLantern = !!crew && me.connected && !me.carrying && !me.action && !this.holding && me.recoverIn <= 0 &&
      me.attackRemaining <= 0 && this.attackPrediction <= 0 && me.staggerRemaining <= 0 &&
      me.pistolRecoveryRemaining <= 0 && me.pistolReloadRemaining <= 0 && !me.blocking &&
      body.mode !== MODE_SWIM && !body.sprinting && body.slideRemaining <= 0;
    for (const item of LANTERN_INTERACTIONS) {
      if (Math.hypot(item.x - eye.x, item.z - eye.z) > INTERACT.promptRange + PLAYER.radius) continue;
      if (item.id === "cargo" && !lantern.revealed) continue;
      let label = `E  ${item.prompt}`;
      let enabled = freeLantern;
      switch (item.id) {
        case "bearing":
          label = `E  Turn signal bearing · ${LANTERN_BEARINGS[lantern.bearing]}`;
          break;
        case "colour":
          label = `E  Change signal light · ${LANTERN_COLOURS[lantern.colour]}`;
          break;
        case "signal":
          if (!crew || (crew.lanternClues & (LANTERN_RULES.clueJournal | LANTERN_RULES.clueMarkings)) !== (LANTERN_RULES.clueJournal | LANTERN_RULES.clueMarkings)) {
            label = "Signal clues: read the keeper's journal and Gull Rock markings";
            enabled = false;
          } else label = "E  Send the keeper's safe signal";
          break;
        case "cargo":
          if (lantern.cargoTaken) { label = "Recovered cargo already claimed"; enabled = false; }
          break;
        case "cache":
          if (lantern.cacheTaken) { label = "This hiding place is empty"; enabled = false; }
          break;
        case "supplies":
          if (lantern.supplyCooldown > 0) { label = `Supplies replenishing · ${Math.ceil(lantern.supplyCooldown)}s`; enabled = false; }
          else if (!crew?.ship.anchored || Math.hypot(crew.ship.x - item.x, crew.ship.z - item.z) > 60) {
            label = "Anchor your crew's ship within 60m to collect supplies";
            enabled = false;
          }
          break;
      }
      if (!freeLantern && enabled === false && label.startsWith("E  ")) {
        label = me.carrying ? "Put down your chest to use this" : "Stand still with hands free to use this";
      }
      cands.push({ id: `lantern:${item.id}`, hold: false, label, x: item.x, y: item.y, z: item.z, visible: true, enabled });
    }
    for (const barrel of SUPPLY_BARRELS) {
      const p = supplyBarrelWorld(barrel.id);
      cands.push({ id: `barrel:${barrel.id}`, hold: false, label: "E  Take supplies", x: p.x, y: p.y + 0.7, z: p.z });
    }
    for (const outpost of OUTPOSTS) {
      cands.push({
        id: "questBoard", hold: false,
        label: this.ownCrew()?.quest.status === "none" ? "E  Choose a treasure quest" : "E  View quest",
        x: outpost.questBoard.x, y: outpost.questBoard.y + 1.4, z: outpost.questBoard.z,
      });
      cands.push({
        id: "merchant", hold: false,
        label: me.carrying ? "E  Sell the treasure" : "Merchant: bring treasure here",
        x: outpost.merchant.x, y: outpost.merchant.y + 1, z: outpost.merchant.z,
      });
    }
    if (!me.carrying) {
      s.chests.forEach((c, id) => {
        if (c.loc === CHEST_CARRIED) return;
        const root = this.chests.get(id)?.root;
        if (!root) return;
        const p = root.getAbsolutePosition();
        cands.push({ id: `chest:${id}`, hold: false, label: "E  Pick up the chest", x: p.x, y: (c.loc === CHEST_FLOATING ? c.y : p.y) + 0.4, z: p.z, visible: c.loc !== CHEST_SHIP });
      });
    }
    for (let i = 0; i < GATE_LEVERS.length; i++) {
      const lever = GATE_LEVERS[i]!;
      cands.push({ id: `lever:${i}`, hold: false, label: s.gates[lever.gate] ? "E  Pull the lever to close the gate" : "E  Pull the lever to open the gate", x: lever.at.x, y: lever.at.y + 1, z: lever.at.z });
    }
    for (let i = 0; i < TELESCOPES.length; i++) {
      const scope = TELESCOPES[i]!;
      let user: string | undefined;
      s.players.forEach((p) => { if (p.station === `telescope:${i}`) user = p.name; });
      cands.push({ id: `telescope:${i}`, hold: false, label: user ? `Telescope in use by ${user}` : "E  Look through the telescope", x: scope.at.x, y: scope.at.y + 1.4, z: scope.at.z });
    }
    for (let i = 0; i < LOOT_CRATES.length; i++) {
      if ((s.crates[i] ?? 0) > 0) continue;
      const crate = LOOT_CRATES[i]!;
      cands.push({ id: "crate", hold: false, label: me.equipment === "sword" ? "Left click  Smash the crate" : "Draw your cutlass (1) to smash the crate", x: crate.at.x, y: crate.at.y + .5, z: crate.at.z });
    }
    // Capsule position (feet in world frame).
    const feet = this.feetWorld(tmp2);
    let best: Target | null = null;
    let bestD = Infinity;
    for (const c of cands) {
      const chest = c.id.startsWith("chest:");
      const cy = Math.max(feet.y + PLAYER.radius, Math.min(feet.y + bodyHeight(this.predictor.body) - PLAYER.radius, c.y));
      const d = Math.hypot(c.x - feet.x, c.y - cy, c.z - feet.z);
      // Boarding drops cargo at sea level: use the server's pickup reach, not the shorter work prompt range.
      if (d > (chest ? INTERACT.serverRange : INTERACT.promptRange)) continue;
      const vx = c.x - eye.x;
      const vy = c.y - eye.y;
      const vz = c.z - eye.z;
      const len = Math.hypot(vx, vy, vz) || 1;
      const cos = (vx * fwd.x + vy * fwd.y + vz * fwd.z) / len;
      // Up close anything counts, except lanterns: the stern one hangs just behind the helmsman.
      if (cos < Math.cos(INTERACT.coneHalfAngle) && (len > 1.2 || c.id.startsWith("shipLantern:"))) continue;
      // Loose cargo keeps its existing ship-side reach; authored island walls still occlude it.
      if (c.visible && !worldVisible(eye, c, chest ? undefined : this.renderPoses)) continue;
      if (d < bestD || (best?.enabled === false && chest)) {
        bestD = d;
        best = c;
      }
    }
    this.target = best;
    if (best?.enabled === false) this.target = null;
    if (best && ((best.id === "merchant" && !me.carrying) || best.id === "crate")) this.target = null;
    if (best?.label.startsWith("Wheel in use") || best?.label.startsWith("Cannon in use") || best?.label.startsWith("Telescope in use")) this.target = null;
    const label = best ? best.label : this.canDig(me) && this.ownCrew()?.quest.status === "active" ? "Left click  Dig" : null;
    this.hud.setPrompt(bucketHint && label ? `${bucketHint} · ${label}` : bucketHint ?? label, null);
  }

  /** What a left click does with the held bucket here: scoop deck flood water, or throw a full one over the rail. */
  private bucketAction(me: PlayerState): "fill" | "throw" | "carry" | null {
    const b = this.predictor.body;
    if (me.equipment !== "bucket" || me.action || me.station || me.dead || this.flightActive || b.frame === FRAME_WORLD || b.mode !== MODE_WALK) return null;
    const ship = this.room.state.crews.get(String(frameSlot(b.frame)))?.ship;
    if (!ship || ship.sinking) return null;
    if (me.bucketFull) return overboardThrow(b.x, b.z, b.yaw, this.throwEdge) ? "throw" : "carry";
    return ship.flooding > SHIP.bucketMinFlood && overDeck(b.x, b.z) && b.y <= SHIP.deckHeight + 0.05 ? "fill" : null;
  }

  private feetWorld(out: Vec3): Vec3 {
    const b = this.predictor.body;
    const pose = this.renderPoses[frameSlot(b.frame)];
    if (pose) return localToWorld(pose, b.x, b.y, b.z, out);
    out.x = b.x;
    out.y = b.y;
    out.z = b.z;
    return out;
  }

  /** A flat ship-frame world point on `slot`'s ship, moved with its hull on the waves; other slots pass through. */
  private ride(slot: number, flat: Readonly<Vec3>, out: Vec3): Vec3 {
    const ride = this.ships.get(slot)?.ride;
    if (!ride) {
      out.x = flat.x; out.y = flat.y; out.z = flat.z;
      return out;
    }
    Vector3.TransformCoordinatesFromFloatsToRef(flat.x, flat.y, flat.z, ride, this.ridePoint);
    out.x = this.ridePoint.x; out.y = this.ridePoint.y; out.z = this.ridePoint.z;
    return out;
  }

  // ---------------------------------------------------------------------------
  // HUD

  private updateHud(me: PlayerState | undefined): void {
    this.hud.setEquipment((me?.equipment ?? "shovel") as Equipment, !!me?.blocking);
    this.hud.setPistol(!!me && !me.dead && me.crew >= 0 && me.equipment === "pistol",
      me?.pistolRounds ?? 0, me?.pistolReserve ?? 0,
      me ? Math.max(0, me.pistolReloadRemaining - this.patchAge()) : 0,
      me ? Math.max(0, me.pistolRecoveryRemaining - this.patchAge()) : 0);
    const body = this.predictor.body;
    this.hud.setLocomotion(body.slideRemaining > 0 ? "slide" : body.sprinting ? "sprint" : body.crouched ? "crouch" : "");
    this.hud.ref("equipmentHint").hidden = !me || me.dead || this.flightActive || !!me.station || !!me.carrying || !!me.action || me.mode === MODE_SWIM || me.recoverIn > 0;
    const s = this.room.state;
    const own = this.ownCrew();
    this.hud.setLanternJournal(own?.slot ?? -1, own?.lanternDiscoveries ?? 0, own?.lanternClues ?? 0, own?.lanternSolved ?? false, s.lantern);
    const onLantern = landmassAt(this.feetWorld(tmp).x, tmp.z)?.id === "lantern";
    const lanternObjective = !own || !(own.lanternClues & LANTERN_RULES.clueJournal)
      ? "Find the keeper's cottage and read the journal (E)."
      : !(own.lanternClues & LANTERN_RULES.clueMarkings)
        ? "Follow the path to Gull Rock and read the safe-signal markings."
        : !s.lantern.revealed
          ? "Climb the lighthouse's interior stairs. Set its bearing and light from your collected clues, then send the signal."
          : !s.lantern.cargoTaken
            ? "Recover the revealed cargo beside the western wreck."
            : "Explore the remaining trails or carry recovered treasure to an outpost merchant.";
    this.hud.setIslandObjective(onLantern && own?.quest.status !== "none" ? lanternObjective : null);
    if (this.hud.onlineOpen && this.onlineView && performance.now() - this.onlineAt >= 500) {
      this.onlineAt = performance.now();
      this.hud.setOnline(this.onlineView);
    }
    this.hud.setGold(own?.gold ?? 0, own?.titleIndex ?? 0);
    const underwater = this.predictor.body.mode === MODE_SWIM && this.predictor.body.y + 1.6 < -0.05;
    if (me) {
      this.hud.setVitals(me.health, underwater ? this.predictor.body.breath : null, me.carrying !== "");
      // Every damage source (cannonball, fall, drowning) flashes the hurt vignette, not only blades and shots.
      if (me.health < this.lastHealth - 0.5) this.hud.showMeleeFeedback("hurt");
      this.lastHealth = me.health;
    }
    this.hud.setDeath(me?.dead ? me.respawnIn : null);
    if (this.royale) {
      this.updateRoyaleHud(me, own);
      return;
    }
    if (!own) {
      this.hud.setQuestMap(null, 0);
      this.hud.setObjective("Join a crew to begin your adventure.", null);
      return;
    }
    const ship = own.ship;
    const stationShip = s.crews.get(String(frameSlot(me?.frame ?? 0)))?.ship;
    const helm = me?.station === "wheel" && stationShip ? { speed: Math.abs(stationShip.speed), heading: stationShip.heading, sail: stationShip.sail } : null;
    this.hud.setShip(ship, helm, ship.anchored, ship.docked, homeOutpost(own.slot).name);
    const shoreId = me?.station.startsWith("islandCannon:") ? me.station.slice(13) : "";
    const reload = shoreId ? (s.islandCannons.get(shoreId)?.reload ?? 0) :
      me?.station.startsWith("cannon:") ? (stationShip?.cannons.at(Number(me.station.slice(7)))?.reload ?? 0) : null;
    this.hud.setSupplies(ship.cannonballs, ship.planks, reload, me?.cannonLoaded ?? false);
    this.hud.setSinking(ship.sinking ? ship.sinkIn : null, (n) => `Cargo floats where the ship sank. A new ship awaits at ${homeOutpost(own.slot).name} in ${n}… Your gold is safe.`);
    if (ship.sinking) this.hud.closeSale();
    if (this.hud.paused) this.hud.setMenuState(!!me && !me.dead && !this.flightActive && me.recoverIn <= 0, own.quest.status !== "none");
    this.hud.setBanner("recover", me && me.recoverIn > 0 ? `Recovering in ${Math.ceil(me.recoverIn)}… stay still.` : null);
    const q = own.quest;
    const island = isIslandId(q.island) ? getIsland(q.island) : null;
    this.hud.setQuestMap(q.status !== "none" && island ? island.id : null, q.spot);
    let objective: string;
    let hint: string | null = null;
    if (q.status === "none") {
      objective = onLantern ? lanternObjective : "Visit a quest board at any outpost.";
      if (onLantern) hint = "E: interact · M: map & crew exploration journal";
    } else if (q.status === "active" && island) {
      const onIsland = landmassAt(this.feetWorld(tmp).x, tmp.z)?.id === island.id;
      objective = onIsland ? `Find the X on ${island.name} and dig (left click).` : `Sail to ${island.name} (see map: M).`;
      if (q.kind === "hunt") objective = `Clue ${q.leg + 1}/${q.legs}: ${objective}`;
      hint = q.dig > 0 ? `Digging progress: ${q.dig}/${INTERACT.digHits}` : (island.digSpots[q.spot]?.hint ?? null);
    } else {
      const chest = s.chests.get(q.chestId);
      if (chest?.carrier === this.room.sessionId) objective = "Carry the chest to a merchant at any outpost.";
      else if (chest?.loc === CHEST_CARRIED) objective = `${s.players.get(chest.carrier)?.name ?? "A crewmate"} is carrying the treasure.`;
      else if (chest?.loc === CHEST_SHIP) objective = "Treasure aboard! Sail to any outpost and sell it.";
      else objective = "Pick up the treasure chest and bring it aboard.";
      this.completeStep("dig");
    }
    this.hud.setObjective(objective, hint);
    if (q.status !== "none") this.completeStep("quest");
    if (this.predictor.body.frame > 0) this.completeStep("board");
    if (me?.station === "wheel") this.completeStep("wheel");

    // Vote banner.
    const v = own.vote;
    if (v.active) {
      const initiator = s.players.get(v.initiator)?.name ?? "A crewmate";
      const iVoted = v.yes.includes(this.room.sessionId);
      this.hud.setBanner(
        "vote",
        iVoted ? `Abandon vote: waiting for your crewmate (${Math.ceil(v.endsIn)}s)` : `${initiator} wants to abandon the quest. Press Y to agree or N to keep it (${Math.ceil(v.endsIn)}s)`,
      );
    } else {
      this.hud.setBanner("vote", null);
    }

    // Boundary warning (ship or player).
    const feet = this.feetWorld(tmp);
    const lim = WORLD.half - WORLD.boundaryWarning;
    const pose = this.renderPoses[own.slot];
    const near = Math.max(Math.abs(feet.x), Math.abs(feet.z)) > lim || (!!pose && Math.max(Math.abs(pose.x), Math.abs(pose.z)) > lim);
    this.hud.setBanner("boundary", near ? "" : null);

    if (this.hud.mapOpen) {
      const markers: MapMarker[] = [];
      if (pose) markers.push({ x: pose.x, z: pose.z, heading: pose.heading, label: "Your ship", kind: "ship" });
      markers.push({ x: feet.x, z: feet.z, heading: this.camera.absoluteRotation.toEulerAngles().y, label: "You", kind: "self" });
      for (const [id, r] of this.remotes) {
        const p = s.players.get(id);
        if (!p || p.dead || p.crew !== me?.crew) continue;
        const wp = r.avatar.root.getAbsolutePosition();
        markers.push({ x: wp.x, z: wp.z, heading: 0, label: p.name, kind: "mate" });
      }
      this.hud.setMapMarkers(markers);
    }
  }

  private completeStep(step: OnboardingStep): void {
    if (settings.completeStep(step)) this.hud.renderOnboarding();
  }

  // ---------------------------------------------------------------------------
  // Feedback

  private onAttackAck(m: AttackAckMsg): void {
    if (m.requestId === this.pendingQueueRequest) {
      this.pendingQueueRequest = 0;
      this.queuedSerial = m.status === "queued" ? m.serial : 0;
    }
    if (m.requestId !== this.pendingAttackRequest) return;
    this.pendingAttackRequest = 0;
    if (m.status === "rejected") {
      if (this.attackClockSerial === this.predictedSerial) this.attackClock = -1;
      this.attackPrediction = 0;
      this.predictedSerial = 0;
    } else if (m.status === "started") {
      if (this.attackClockSerial === this.predictedSerial) this.attackClockSerial = m.serial;
      this.predictedSerial = m.serial;
    }
  }

  private onMeleeResult(m: MeleeResultMsg): void {
    const previous = this.meleeSeen.get(m.attacker);
    if (previous !== undefined && ((m.serial - previous) >>> 0) >= 0x80000000) return;
    if (previous === m.serial) return;
    this.meleeSeen.set(m.attacker, m.serial);
    if (m.attacker === this.room.sessionId) {
      if (m.result === "hit") this.strikeHitSerial = m.serial;
      this.recoil = m.result === "hit" ? 0.035 : m.result === "blocked" ? 0.07 : m.result === "parried" ? 0.10 : 0;
      if (m.result !== "hit") this.queuedSerial = 0;
      if (m.result !== "miss") this.hud.showMeleeFeedback(m.result);
      // A landed finisher jolts the attacker's view on impact.
      if (m.result === "hit" && m.step === 2) this.firstPerson.shake(0.3);
    }
    const remoteAttacker = this.remotes.get(m.attacker), remoteDefender = this.remotes.get(m.target);
    if (remoteAttacker) remoteAttacker.recoil = m.result === "hit" ? 0.035 : m.result === "blocked" ? 0.07 : m.result === "parried" ? 0.10 : 0;
    if (remoteDefender && m.result === "parried") remoteDefender.parryRaise = 1;
    // Presentation-only flinch on whoever was struck; the server applies no knockback.
    if (m.result === "hit") {
      if (remoteDefender) { remoteDefender.hitFlinch = 0; remoteDefender.hitStep = m.step; }
      if (m.target === this.room.sessionId) { this.hitFlinch = 0; this.hitStep = m.step; }
    }
    if (m.result === "miss") return;
    const cam = this.camera.globalPosition;
    const distance = Math.hypot(m.x - cam.x, m.y - cam.y, m.z - cam.z);
    audio.play(m.result === "hit" ? m.step === 2 ? "swordHitHeavy" : "swordHit" : m.result === "parried" ? "swordParry" : "swordBlock", { position: m });
    if (distance < 60) this.effects.melee(m.result, m.x, m.y, m.z, m.step);
    if (m.target === this.room.sessionId) {
      if (m.result === "hit") this.hud.showMeleeFeedback("hurt");
      if (m.result === "parried") { this.parryRaise = 1; this.hud.showMeleeFeedback("parried"); }
    }
  }

  private onPistolShot(m: PistolShotMsg): void {
    const previous = this.pistolSeen.get(m.shooter);
    if (previous !== undefined && (previous === m.serial || ((m.serial - previous) >>> 0) >= 0x80000000)) return;
    this.pistolSeen.set(m.shooter, m.serial);
    const local = m.shooter === this.room.sessionId;
    const remote = this.remotes.get(m.shooter);
    if (local) this.pistolRecoil = 1;
    else if (remote) remote.pistolRecoil = 1;
    audio.play("pistolShot", { position: m });
    const muzzle = this.pistolMuzzle;
    muzzle.set(m.x, m.y, m.z);
    if (local && this.selfPose.visible && this.selfPose.equipment === "pistol" && this.selfPose.action === "idle") this.selfAvatar.pistolMuzzleToRef(muzzle);
    else if (remote?.avatar.root.isEnabled()) {
      const shooter = this.room.state.players.get(m.shooter);
      if (shooter?.equipment === "pistol" && !shooter.station && !shooter.carrying &&
        !shooter.action && shooter.mode !== MODE_SWIM) remote.avatar.pistolMuzzleToRef(muzzle);
    }
    this.effects.event("pistol", muzzle.x, muzzle.y, muzzle.z);
    if (m.impact !== "none") this.effects.event(m.impact === "player" ? "playerHit" : m.impact === "water" ? "splash" : "pistolImpact", m.hitX, m.hitY, m.hitZ);
    if (m.target) {
      if (local) this.hud.showMeleeFeedback("hit");
      if (m.target === this.room.sessionId) this.hud.showMeleeFeedback("hurt");
    }
  }


  private onFx(m: FxMsg): void {
    const cam = this.camera.globalPosition;
    const dist = Math.hypot(m.x - cam.x, m.y - cam.y, m.z - cam.z);
    const power = m.power ?? 1;
    const sfx = FX_SOUND[m.kind];
    if (sfx) audio.play(sfx, { position: m, intensity: power });
    const aboard = frameSlot(this.predictor.body.frame);
    const me = this.me();
    if (m.kind === "cannon") {
      // Far shots: the flash arrives first, the boom after its travel time.
      if (dist < CANNON_NEAR) audio.play("cannon", { position: m });
      else window.setTimeout(() => audio.play("cannonFar", { position: m }), (dist / SPEED_OF_SOUND) * 1000);
      if (dist < 5 && me?.station.startsWith("cannon:")) this.cannonRecoil = 1;
      for (const [id, remote] of this.remotes) {
        if (this.room.state.players.get(id)?.station.startsWith("cannon:") &&
          Math.hypot(remote.avatar.root.position.x - m.x, remote.avatar.root.position.y - m.y, remote.avatar.root.position.z - m.z) < 5) remote.cannonRecoil = 1;
      }
      if (dist < 15) this.firstPerson.shake();
    } else if (m.kind === "impact" || m.kind === "ram") {
      // Everyone aboard either hull feels the blow, scaled by the collision speed.
      const felt = aboard >= 0 && (m.ship === aboard || m.by === aboard);
      if (felt) this.firstPerson.shake(0.4 + 0.6 * power);
      else if (dist < 20) this.firstPerson.shake(power);
    } else if (m.kind === "ballHit") {
      if (aboard >= 0 && m.ship === aboard) this.firstPerson.shake(0.7);
      else if (dist < 20) this.firstPerson.shake(0.4);
    }
    // Your crew's ball found an enemy hull or sailor: confirm it at any range.
    if ((m.kind === "ballHit" || m.kind === "playerHit") && me && me.crew >= 0 && m.by === me.crew) {
      audio.play("hitConfirm");
      if (me.station.startsWith("cannon:")) this.hud.showMeleeFeedback("hit");
    }
    if ((m.kind === "gunWreck" || m.kind === "wheelSmash") && dist < 10) this.firstPerson.shake(0.5);
    this.effects.event(m.kind, m.x, m.y, m.z);
  }

  /** The Character creator saved a new look in another tab: wear it now. */
  private readonly onLookSaved = (event: StorageEvent): void => {
    if (event.key === LOOK_STORAGE_KEY) this.room.send(C2S.look, savedLookCode());
  };
  private readonly onEscape = (event: KeyboardEvent): void => {
    if (event.code !== "Escape" || event.repeat) return;
    if (this.hud.dialogOpen) {
      event.preventDefault();
      this.hud.closeDialogs();
      this.input.overlay = this.hud.mapOpen || this.hud.dialogOpen;
      if (!this.input.overlay && !this.input.locked) this.showPause();
    } else if (this.hud.mapOpen) {
      event.preventDefault();
      this.hud.setMapOpen(false);
      this.input.overlay = false;
      if (!this.input.locked) this.showPause();
    }
  };

  private onDigResult(m: DigResultMsg): void {
    switch (m.result) {
      case "nothing":
        this.hud.toast("info", "Nothing here. Check the treasure map (M).");
        break;
      case "noQuest":
        this.hud.toast("info", "Nothing to dig for here.");
        break;
      case "progress":
        this.hud.toast("info", `Digging… ${m.progress}/${INTERACT.digHits}`);
        break;
      case "clue":
        this.hud.toast("good", "Clue found! Check the map for the next island.");
        break;
      case "found":
        this.hud.toast("good", "Treasure found!");
        this.completeStep("dig");
        break;
      case "cooldown":
      case "invalid":
        break;
    }
  }

  private updateAudio(dt: number, me: PlayerState | undefined): void {
    const flooding = this.ownCrew()?.ship.flooding ?? 0;
    if (flooding > 70 && this.lastFlooding <= 70) this.hud.toast("bad", "The ship is filling with water! Repair leaks and bail!");
    this.lastFlooding = flooding;

    // Listener follows the final camera pose (left-handed: yaw 0 looks +Z).
    const cam = this.camera.position;
    const yaw = this.camera.rotation.y, pitch = this.camera.rotation.x;
    const sy = Math.sin(yaw), cy = Math.cos(yaw), sp = Math.sin(pitch), cp = Math.cos(pitch);
    Object.assign(this.listenerForward, { x: sy * cp, y: -sp, z: cy * cp });
    Object.assign(this.listenerUp, { x: sy * sp, y: cp, z: cy * sp });
    audio.setListener(cam, this.listenerForward, this.listenerUp);
    this.updateAmbience(cam);

    const b = this.predictor.body;
    const sliding = b.slideRemaining > 0;
    const alive = !!me && me.connected && !me.dead && me.crew >= 0;
    if (!me || !alive || !audio.running) {
      // Nothing accumulates while silent (hidden tab, locked autoplay, dead).
      this.pendingSteps = this.pendingStrokes = this.pendingLanding = 0;
      this.heldAction.reset();
      this.wasSliding = sliding;
      return;
    }
    const feet = this.feetWorld(this.audioPos);
    const surface = this.pendingSteps > 0 || this.pendingLanding > 0 || (sliding && !this.wasSliding) ? surfaceAt(b.frame, b.x, b.y, b.z) : "wood";
    const intensity = b.crouched ? 0.4 : b.sprinting ? 1 : me.carrying ? 0.6 : 0.8;
    for (; this.pendingSteps > 0; this.pendingSteps--) audio.play("step", { position: feet, surface, intensity });
    if (this.pendingLanding > 0) audio.play("land", { position: feet, surface, intensity: this.pendingLanding });
    this.pendingLanding = 0;
    // Surface strokes only; a fully submerged swimmer hears the underwater bed instead.
    if (this.pendingStrokes > 0 && cam.y > -0.5) audio.play("swim", { position: feet });
    this.pendingStrokes = 0;
    if (sliding && !this.wasSliding) audio.play("slide", { position: feet, surface });
    this.wasSliding = sliding;
    const held = this.heldAction.advance(me.action, true, dt, me.actionProgress);
    if (held) audio.play(ACTION_SFX[held], { position: feet });
    const now = performance.now();
    if (b.breath < 6 && b.mode === MODE_SWIM && now - this.lastDrownSound > 1200) {
      this.lastDrownSound = now;
      audio.play("drowning");
    }
  }

  /** Movement-audio snapshot of the predicted local body. */
  private captureMotion(out: MotionSample, me: PlayerState): void {
    const b = this.predictor.body;
    out.frame = b.frame;
    out.generation = this.ships.get(frameSlot(b.frame))?.generation ?? -1;
    out.mode = b.mode;
    out.x = b.x;
    out.y = b.y;
    out.z = b.z;
    out.grounded = b.grounded;
    out.enabled = audio.running && me.connected && !me.dead;
    out.carrying = me.carrying !== "";
    out.sliding = b.slideRemaining > 0;
    out.crouched = b.crouched;
    out.sprinting = b.sprinting;
    out.station = me.station;
  }

  /** Chest-height point for the local sword swing. */
  private swingPosition(): Vec3 {
    const p = this.feetWorld(this.audioPos);
    p.y += 1.1;
    return p;
  }

  /** Footfalls, strokes, landings and held actions of another player, at their rendered feet. */
  private remoteAudio(r: Remote, p: PlayerState, generation: number, position: Readonly<Vec3>, dt: number): void {
    const enabled = audio.running && p.connected && !p.dead && p.crew >= 0;
    const now = r.motionNow;
    now.frame = r.sample.frame;
    now.generation = generation;
    now.mode = r.sample.mode;
    now.x = r.sample.x;
    now.y = r.sample.y;
    now.z = r.sample.z;
    now.grounded = r.sample.mode !== MODE_LAUNCH && p.grounded;
    now.enabled = enabled;
    now.carrying = p.carrying !== "";
    now.sliding = p.slideRemaining > 0;
    now.crouched = p.crouched;
    now.sprinting = p.sprinting;
    now.station = r.sample.station;
    r.motion.advance(r.motionPrev, now, dt, this.cues);
    r.motionNow = r.motionPrev;
    r.motionPrev = now;
    // The interpolated sample the rig draws, so remote nail strikes land with the hammer.
    const held = r.heldAction.advance(r.sample.mode === MODE_LAUNCH || r.sample.cannonLoaded ? "" : r.sample.action, enabled, dt, r.sample.actionProgress);
    const cues = this.cues;
    if (!cues.steps && !cues.strokes && !cues.landing && !held) return;
    const surface = cues.steps || cues.landing ? surfaceAt(now.frame, now.x, now.y, now.z) : "wood";
    const intensity = p.crouched ? 0.4 : p.sprinting ? 1 : p.carrying ? 0.6 : 0.8;
    for (let i = 0; i < cues.steps; i++) audio.play("step", { position, surface, intensity });
    if (cues.landing) audio.play("land", { position, surface, intensity: cues.landing });
    if (cues.strokes) audio.play("swim", { position });
    if (held) audio.play(ACTION_SFX[held], { position });
  }

  /** Ambience: underwater state, shoreline proximity (5 Hz) and the nearest ships' state. */
  private updateAmbience(cam: Readonly<Vec3>): void {
    const now = performance.now();
    if (now >= this.shoreAt) {
      this.shoreAt = now + 200;
      this.ambience.shore = shoreProximity(cam.x, cam.z);
    }
    this.ambience.underwater = this.env.isUnderwater;
    const occupied = frameSlot(this.predictor.body.frame);
    const candidates = this.shipCandidates;
    let n = 0;
    for (const slot of this.ships.keys()) {
      const pose = this.renderPoses[slot];
      if (!pose || !this.room.state.crews.get(String(slot))) continue;
      const d = Math.hypot(pose.x - cam.x, pose.z - cam.z);
      if (d > SHIP_AUDIO_RANGE) continue;
      const c = candidates[n] ??= { slot: 0, d: 0 };
      c.slot = slot;
      // The ship underfoot always keeps its bed.
      c.d = slot === occupied ? -1 : d;
      n++;
    }
    candidates.length = n;
    candidates.sort(byDistanceThenSlot);
    const ships = this.audioShips;
    ships.length = 0;
    for (const c of candidates) {
      if (ships.length >= MAX_SHIP_BEDS) break;
      const pose = this.renderPoses[c.slot]!;
      const s = this.room.state.crews.get(String(c.slot))!.ship;
      const out = this.shipAudioPool[ships.length] ??= {
        slot: 0, generation: 0, x: 0, y: 0, z: 0, speed: 0, sail: 0, anchored: false, flooding: 0, leaks: 0, sinking: false, sinkIn: 0,
      };
      out.slot = c.slot;
      out.generation = this.ships.get(c.slot)?.generation ?? 0;
      out.x = pose.x;
      out.y = SHIP.deckHeight;
      out.z = pose.z;
      out.speed = s.speed;
      out.sail = s.sail;
      out.anchored = s.anchored;
      out.flooding = s.flooding;
      out.leaks = s.leaks;
      out.sinking = s.sinking;
      out.sinkIn = s.sinkIn;
      ships.push(out);
    }
    audio.updateAmbience(this.ambience);
  }


  private projectedWidth(root: TransformNode): number {
    const bounds = root.getHierarchyBoundingVectors();
    this.viewport.width = this.engine.getRenderWidth();
    this.viewport.height = this.engine.getRenderHeight();
    let left = Infinity, right = -Infinity;
    for (let corner = 0; corner < 8; corner++) {
      this.tagWorld.set(corner & 1 ? bounds.max.x : bounds.min.x,
        corner & 2 ? bounds.max.y : bounds.min.y, corner & 4 ? bounds.max.z : bounds.min.z);
      Vector3.ProjectToRef(this.tagWorld, Matrix.IdentityReadOnly, this.scene.getTransformMatrix(), this.viewport, this.tagScreen);
      if (this.tagScreen.z <= 0 || this.tagScreen.z >= 1) return 0;
      left = Math.min(left, this.tagScreen.x);
      right = Math.max(right, this.tagScreen.x);
    }
    return right - left;
  }

  private exposeTestHooks(): void {
    const hooks = {
      play: () => {
        this.input.forceActive = true;
        this.hud.setPaused(false, "");
      },
      setInput: (i: AutoInput | null) => {
        this.autoInput = i;
      },
      look: (yaw: number, pitch = 0) => {
        this.input.yaw = yaw;
        this.input.pitch = pitch;
      },
      send: (type: string, payload: unknown) => this.room.send(type, payload),
      fire: () => this.room.send(C2S.fire, {}),
      crew: (choice: CrewChoice) => this.room.send(C2S.crew, { choice }),
      use: (target: UseTarget) => {
        if (!this.flightActive) this.room.send(C2S.use, { target });
      },
      lastDebug: () => this.lastDebug,
      sessionId: () => this.room.sessionId,
      /** Rendered world positions at the current render tick. */
      rendered: () => {
        const out: Record<string, {
          x: number; y: number; z: number; frame: number; sampleTick: number; mode: number;
          station: string; cannonLoaded: boolean; launchSerial: number; yaw: number; pitch: number;
        }> = {};
        for (const [id, r] of this.remotes) {
          const p = r.avatar.root.getAbsolutePosition();
          const sample = r.sample;
          out[id] = { x: p.x, y: p.y, z: p.z, frame: sample.frame, sampleTick: sample.tick, mode: sample.mode,
            station: sample.station, cannonLoaded: sample.cannonLoaded, launchSerial: sample.launchSerial,
            yaw: sample.yaw, pitch: sample.pitch };
        }
        const ships: Record<string, { x: number; z: number; yaw: number }> = {};
        for (const [slot, client] of this.ships) ships[String(slot)] = { x: client.pose.x, z: client.pose.z, yaw: client.pose.yaw };
        return { renderTick: this.lastRenderTick, ships, remotes: out };
      },
      self: () => {
        const p = this.feetWorld({ x: 0, y: 0, z: 0 });
        const b = this.predictor.body;
        const authority = this.me();
        return { ...p, frame: b.frame, mode: b.mode, localX: b.x, localZ: b.z, yaw: this.input.yaw,
          vx: b.vx, vy: b.vy, vz: b.vz, launchTicks: b.launchTicks,
          launchSerial: authority?.launchSerial ?? 0, cannonLoaded: authority?.cannonLoaded ?? false,
          crouched: b.crouched, sprinting: b.sprinting, sprintTime: b.sprintTime, slideRemaining: b.slideRemaining,
          slideYaw: b.slideYaw, slideCooldown: b.slideCooldown, crouchHeld: b.crouchHeld,
          authoritative: authority ? {
            x: authority.x, y: authority.y, z: authority.z, frame: authority.frame, mode: authority.mode,
            vx: authority.vx, vy: authority.vy, vz: authority.vz, launchTicks: authority.launchTicks,
            launchSerial: authority.launchSerial, cannonLoaded: authority.cannonLoaded,
            crouched: authority.crouched, sprinting: authority.sprinting, sprintTime: authority.sprintTime,
            slideRemaining: authority.slideRemaining, slideYaw: authority.slideYaw,
            slideCooldown: authority.slideCooldown, crouchHeld: authority.crouchHeld,
          } : null };
      },
      // Bounds are sampled only on explicit debug calls, never in the render loop.
      presentation: () => ({
        fov: this.camera.fov, eye: this.eyeHeight, roll: this.camera.rotation.z,
        scopeBlend: this.scopeBlend, magnification: this.magnification,
        attackStep: this.displayAttackStep, attackPhase: this.attackPhase,
        attackDuration: this.displayAttackDuration, hitFlinch: this.hitFlinch,
        rigAction: this.selfPose.action, rigVisible: this.selfPose.visible && !this.thirdPersonActive, cannonRecoil: this.cannonRecoil,
        rigSwim: this.selfAvatar.swimState(),
        ships: Array.from(this.ships, ([slot, ship]) => ({ slot, projectedWidth: this.projectedWidth(ship.model.root) })),
        remotes: Array.from(this.remotes, ([id, remote]) => ({
          id, visible: remote.avatar.root.isEnabled(), headHeight: remote.avatar.head.getAbsolutePosition().y - remote.avatar.root.position.y,
          sampleTick: remote.sample.tick, frame: remote.sample.frame, mode: remote.sample.mode,
          station: remote.sample.station, cannonLoaded: remote.sample.cannonLoaded, launchSerial: remote.sample.launchSerial,
          rigAction: remote.pose.action, rigSwim: remote.avatar.swimState(),
          attackStep: remote.pose.attackStep, attackPhase: remote.pose.attackPhase, hitFlinch: remote.pose.hitFlinch,
          contactErrors: remote.avatar.diagnostics().map((limb) => limb.contactError),
        })),
        arms: this.selfAvatar.diagnostics().map((arm) => ({
          upperLength: Vector3.Distance(arm.shoulder, arm.elbow),
          lowerLength: Vector3.Distance(arm.elbow, arm.wrist),
          contactError: arm.contactError,
          shoulder: arm.shoulder.asArray(), elbow: arm.elbow.asArray(),
          wrist: arm.wrist.asArray(), contact: arm.contact.asArray(),
        })),
      }),
      state: () => this.room.state.toJSON(),
      target: () => this.target?.id ?? null,
      fps: () => this.fpsSamples.length / this.fpsSamples.reduce((a, b) => a + b, 0),
      interact: () => {
        const me = this.me();
        if (me) this.interact(me);
      },
      release: () => this.stopHold(),
      dig: () => {
        const me = this.me();
        if (me && this.canDig(me)) this.room.send(C2S.dig, {});
      },
      dropConnection: () => this.room.connection.close(),
      weather: () => ({ ...this.weatherSample }),
      overrideWeather: (override: Partial<WeatherSample> | null) => { this.weatherOverride = override; },
    };
    Object.assign(window, { __tinySeas: hooks });
  }

  netProbe(): NetProbe {
    return {
      room: this.room,
      clock: this.clock,
      predictor: this.predictor,
      renderTick: () => this.lastRenderTick,
      forEachBuffer: (visit) => {
        const own = this.me()?.crew ?? -1;
        for (const [slot, ship] of this.ships) visit(ship.buffer, slot === own ? "own" : "ship");
        for (const remote of this.remotes.values()) visit(remote.buffer, "player");
      },
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    audio.setGameplayActive(false);
    this.resetScope();
    this.engine.stopRenderLoop();
    window.removeEventListener("keydown", this.onEscape);
    window.removeEventListener("storage", this.onLookSaved);
    this.hud.dispose();
    this.selfAvatar.dispose();
    for (const model of this.islandCannons.values()) model.dispose();
    this.islandCannons.clear();
    this.scene.dispose();
    if (document.pointerLockElement) document.exitPointerLock();
  }
}
